import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  AlertTriangle, BarChart3, Bot, Cable, ChevronLeft, ChevronRight, CircleDollarSign, Clapperboard, Download,
  Filter, Gamepad2, KeyRound, Landmark, LogOut, MapPin, Megaphone, PiggyBank,
  Plus, Radio, RefreshCw, Shield, Sparkles, Trash2, TrendingUp, UserCheck, Users, WalletCards, X
} from 'lucide-react';
import { defaultData, marketingData, mgmtAccounts, activeAccounts } from './data';
import AdminAccessPanel from './AdminAccessPanel';
import ActiveAccountsPanel from './ActiveAccountsPanel';
import AgencyAnalytics from './AgencyAnalytics';
import AIReelCreator from './AIReelCreator';
import AuthScreen from './AuthScreen';
import ReelInsightsSection from './ReelInsights';
import SocialAnalyticsDrilldown from './SocialAnalyticsDrilldown';
import SocialConnections from './SocialConnections';
import { DASHBOARD_TABS, getTabLabel, getVisibleTabsForProfile } from './dashboardAccess';
import { loadManagementAccounts } from './lib/managementAccountsApi';
import { loadMergedMarketingData } from './lib/socialAnalyticsApi';
import { acceptDashboardInvite, getProfile, isSupabaseConfigured, loadSessionFromUrl, loadStoredSession, signOut } from './supabaseClient';

const TAB_ICONS = {
  agencyAnalytics: BarChart3,
  revenue: CircleDollarSign,
  subscribers: Users,
  spending: WalletCards,
  marketing: Megaphone,
  reelInsights: Clapperboard,
  aiReelCreator: Bot,
  dataConnections: Cable,
  activeAccounts: UserCheck,
  accounts: Landmark,
  cyprus: MapPin,
  savings: PiggyBank,
  adminAccess: KeyRound
};

function App() {
  const tabStripRef = useRef(null);
  const [activeTab, setActiveTab] = useState('revenue');
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedWeeks, setSelectedWeeks] = useState('all');
  const [selectedRevenueType, setSelectedRevenueType] = useState('total');
  const [selectedMarketingPlatform, setSelectedMarketingPlatform] = useState('all');

  // Comparison mode state
  const [comparisonMode, setComparisonMode] = useState('standard');
  const [compareWeekA, setCompareWeekA] = useState('');
  const [compareWeekB, setCompareWeekB] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');

  const [allData, setAllData] = useState(defaultData);
  const [allMarketingData, setAllMarketingData] = useState(marketingData);
  const [activeAccountRows, setActiveAccountRows] = useState(activeAccounts);
  const [analyticsDataStatus, setAnalyticsDataStatus] = useState({ source: 'historical', liveRows: 0, error: '' });
  const [managementAccounts, setManagementAccounts] = useState(mgmtAccounts);
  const [managementAccountsStatus, setManagementAccountsStatus] = useState({ source: 'snapshot', fetchedAt: '', error: '', loading: false });

  const refreshManagementAccounts = useCallback(async (accessToken) => {
    if (!accessToken) return;
    setManagementAccountsStatus((current) => ({ ...current, loading: true }));
    try {
      const result = await loadManagementAccounts({ accessToken });
      setManagementAccounts(result.accounts);
      setManagementAccountsStatus({ source: result.source, fetchedAt: result.fetchedAt, error: '', loading: false });
    } catch (error) {
      setManagementAccountsStatus((current) => ({ ...current, error: error.message, loading: false }));
    }
  }, []);

  const refreshLiveMarketingData = async (accessToken = session?.access_token) => {
    if (!accessToken) return;
    try {
      const result = await loadMergedMarketingData({
        accessToken,
        dashboardId: 'vasia',
        historicalRows: marketingData
      });
      setAllMarketingData(result.rows);
      setAnalyticsDataStatus({ source: result.source, liveRows: result.liveRows.length, error: '' });
    } catch (error) {
      setAnalyticsDataStatus({ source: 'historical', liveRows: 0, error: error.message });
    }
  };

  const hydrateProfile = async (nextSession) => {
    if (!nextSession?.access_token || !nextSession?.user?.id) return null;
    const loadedProfile = await getProfile(nextSession.access_token, nextSession.user.id);
    const fallbackProfile = {
      id: nextSession.user.id,
      email: nextSession.user.email,
      name: nextSession.user.user_metadata?.full_name || nextSession.user.email,
      role: 'analytics_viewer'
    };
    const resolvedProfile = loadedProfile || fallbackProfile;
    setProfile(resolvedProfile);
    return resolvedProfile;
  };

  useEffect(() => {
    const bootAuth = async () => {
      try {
        if (!isSupabaseConfigured) {
          setProfile({ id: 'local-admin', email: 'local-admin', name: 'Local Admin', role: 'admin' });
          return;
        }

        const urlSession = await loadSessionFromUrl();
        const nextSession = urlSession || loadStoredSession();
        if (nextSession?.access_token) {
          setSession(nextSession);
          const tokenFromUrl = new URLSearchParams(window.location.search).get('invite');
          if (urlSession && tokenFromUrl) {
            await acceptDashboardInvite({
              accessToken: nextSession.access_token,
              inviteToken: tokenFromUrl,
              name: nextSession.user?.user_metadata?.full_name || nextSession.user?.email
            }).catch(() => null);
          }
          await hydrateProfile(nextSession);
        }
      } catch {
        setSession(null);
        setProfile(null);
      } finally {
        setAuthLoading(false);
      }
    };

    bootAuth();
  }, []);

  useEffect(() => {
    const accessToken = session?.access_token;
    if (!accessToken) return undefined;
    refreshManagementAccounts(accessToken);
    const refreshTimer = window.setInterval(() => refreshManagementAccounts(accessToken), 5 * 60 * 1000);
    return () => window.clearInterval(refreshTimer);
  }, [session?.access_token, refreshManagementAccounts]);

  // Cyprus relocation calculator state
  const [cypRev, setCypRev] = useState(330000);
  const [cypNorRent, setCypNorRent] = useState(0);
  const [cypCypRent, setCypCypRent] = useState(3500);

  // Savings goal tracker state
  const [savRent, setSavRent] = useState(2500);
  const [savSavings, setSavSavings] = useState(317094.98);
  const [savWeekly, setSavWeekly] = useState(6500);

  // Cyprus tax math (matches HTML source)
  const NOK_RATE = 11.5;
  const calcNorTax = (profitEur) => {
    const p = profitEur * NOK_RATE;
    const base = p * 0.22;
    const ni = p * 0.108;
    const b = [226100, 318300, 725050, 980100, 1467200];
    const r = [0.017, 0.04, 0.137, 0.168, 0.178];
    let tr = 0;
    for (let i = 0; i < 5; i++) {
      if (p > b[i]) {
        const top = i < 4 ? Math.min(p, b[i + 1]) : p;
        tr += (top - b[i]) * r[i];
      }
    }
    return { base: base / NOK_RATE, ni: ni / NOK_RATE, trinn: tr / NOK_RATE, total: (base + ni + tr) / NOK_RATE };
  };
  const getMarginal = (profitEur) => {
    const p = profitEur * NOK_RATE;
    let tr = 0;
    if (p > 1467200) tr = 0.178;
    else if (p > 980100) tr = 0.168;
    else if (p > 725050) tr = 0.137;
    else if (p > 318300) tr = 0.04;
    else if (p > 226100) tr = 0.017;
    return 0.22 + 0.108 + tr;
  };
  const eur = (v) => '€' + Math.round(Math.abs(v)).toLocaleString();
  const eurS = (v) => (v >= 0 ? '' : '-') + eur(v);

  const [newEntry, setNewEntry] = useState({
    period: '',
    year: '2025',
    vipMessages: '',
    vipTips: '',
    vipSubs: '',
    freeMessages: '',
    vipNew: '',
    vipActive: '',
    freeActive: '',
    vipPerSpender: ''
  });

  // Get available years from data
  const getAvailableYears = () => {
    const years = new Set();
    allData.revenue.forEach(d => years.add(d.year));
    allMarketingData.forEach(d => years.add(d.year));
    return Array.from(years).sort();
  };

  // Filter data based on selected filters
  const getFilteredData = (dataArray) => {
    let filtered = dataArray;
    if (selectedYear !== 'all') {
      filtered = filtered.filter(d => d.year === selectedYear);
    }
    if (selectedWeeks !== 'all') {
      const weekCount = parseInt(selectedWeeks);
      filtered = filtered.slice(-weekCount);
    }
    return filtered;
  };

  const getFilteredMarketingData = () => {
    let filtered = allMarketingData;
    if (selectedYear !== 'all') {
      filtered = filtered.filter(d => d.year === selectedYear);
    }
    if (selectedWeeks !== 'all') {
      const weekCount = parseInt(selectedWeeks);
      filtered = filtered.slice(-weekCount);
    }
    return filtered;
  };

  // Revenue chart data based on filter
  const getRevenueChartData = () => {
    const filtered = getActiveData(allData.revenue);
    return filtered.map(d => {
      switch (selectedRevenueType) {
        case 'messages':
          return { period: d.period, 'VIP Messages': d.vipMessages, 'FREE Messages': d.freeMessages };
        case 'tips':
          return { period: d.period, 'VIP Tips': d.vipTips, 'FREE Tips': d.freeTips };
        case 'subscriptions':
          return { period: d.period, 'VIP Subs': d.vipSubs, 'FREE Subs': d.freeSubs };
        default:
          return { period: d.period, 'VIP Total': d.vipTotal, 'FREE Total': d.freeTotal, 'Overall': d.overall };
      }
    });
  };

  // Calculate percentage change
  const getPercentChange = (current, previous) => {
    if (!previous || previous === 0) return 'N/A';
    return ((current - previous) / previous * 100).toFixed(1) + '%';
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    if (typeof num === 'number') {
      if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
      if (num >= 1000) return num.toLocaleString();
      return num.toFixed(2);
    }
    return num;
  };

  const formatWholeNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString();
  };

  // Get all period strings in order from a data array
  const getAllPeriods = (dataArray) => dataArray.map(d => d.period);

  // Find a single entry by period string
  const getWeekByPeriod = (dataArray, period) => dataArray.find(d => d.period === period);

  // Slice data between two periods (inclusive)
  const getRangeData = (dataArray, startPeriod, endPeriod) => {
    const startIdx = dataArray.findIndex(d => d.period === startPeriod);
    const endIdx = dataArray.findIndex(d => d.period === endPeriod);
    if (startIdx === -1 || endIdx === -1) return dataArray;
    const lo = Math.min(startIdx, endIdx);
    const hi = Math.max(startIdx, endIdx);
    return dataArray.slice(lo, hi + 1);
  };

  // Aggregate numeric fields for a given year
  const getYearAggregates = (dataArray, year, fields, mode = 'sum') => {
    const yearData = dataArray.filter(d => d.year === year);
    const result = { count: yearData.length };
    fields.forEach(f => {
      const sum = yearData.reduce((acc, d) => acc + (d[f] || 0), 0);
      result[f] = mode === 'avg' ? (yearData.length ? sum / yearData.length : 0) : sum;
    });
    return result;
  };

  // Compute diff and percentage change between two values
  const getComparisonDiff = (valA, valB) => {
    const diff = valB - valA;
    const pctChange = valA !== 0 ? ((diff / Math.abs(valA)) * 100) : (valB !== 0 ? 100 : 0);
    return { diff, pctChange, direction: diff >= 0 ? 'up' : 'down' };
  };

  // Get data filtered by range mode or standard filters
  const getActiveData = (dataArray) => {
    if (comparisonMode === 'range' && rangeStart && rangeEnd) {
      return getRangeData(dataArray, rangeStart, rangeEnd);
    }
    return getFilteredData(dataArray);
  };

  const getActiveMarketingData = () => {
    if (comparisonMode === 'range' && rangeStart && rangeEnd) {
      return getRangeData(allMarketingData, rangeStart, rangeEnd);
    }
    return getFilteredMarketingData();
  };

  // Handle adding new week entry
  const handleAddWeek = () => {
    if (!newEntry.period) return;

    const vipMessages = parseFloat(newEntry.vipMessages) || 0;
    const vipTips = parseFloat(newEntry.vipTips) || 0;
    const vipSubs = parseFloat(newEntry.vipSubs) || 0;
    const freeMessages = parseFloat(newEntry.freeMessages) || 0;
    const vipTotal = vipMessages + vipTips + vipSubs;
    const freeTotal = freeMessages;
    const overall = vipTotal + freeTotal;

    const revenueEntry = {
      period: newEntry.period,
      week: `${newEntry.year}-W${String(allData.revenue.length + 1).padStart(2, '0')}`,
      year: newEntry.year,
      vipTotal, freeTotal, overall,
      vipMessages, vipTips, vipSubs,
      freeMessages, freeTips: 0, freeSubs: 0
    };

    const subscriberEntry = {
      period: newEntry.period,
      week: revenueEntry.week,
      year: newEntry.year,
      vipNew: parseInt(newEntry.vipNew) || 0,
      vipActive: parseInt(newEntry.vipActive) || 0,
      freeNew: 0,
      freeActive: parseInt(newEntry.freeActive) || 0,
      vipRenewed: 0,
      freeRenewed: 0,
      vipOFStats: 0,
      vipOFSNewRenew: 0
    };

    const spendingEntry = {
      period: newEntry.period,
      week: revenueEntry.week,
      year: newEntry.year,
      vipPerSpender: parseFloat(newEntry.vipPerSpender) || 0,
      freePerSpender: 0,
      vipPerTransaction: 0,
      freePerTransaction: 0,
      vipNewSubAvg: 0,
      freeNewSubAvg: 0,
      vipActiveFanAvg: 0,
      freeActiveFanAvg: 0
    };

    setAllData(prev => ({
      revenue: [...prev.revenue, revenueEntry],
      subscribers: [...prev.subscribers, subscriberEntry],
      spending: [...prev.spending, spendingEntry]
    }));

    setNewEntry({ period: '', year: newEntry.year, vipMessages: '', vipTips: '', vipSubs: '', freeMessages: '', vipNew: '', vipActive: '', freeActive: '', vipPerSpender: '' });
    setShowUpload(false);
  };

  // Handle deleting a week
  const handleDeleteWeek = (period, year) => {
    if (!window.confirm(`Delete data for ${period} (${year})?`)) return;
    setAllData(prev => ({
      revenue: prev.revenue.filter(d => !(d.period === period && d.year === year)),
      subscribers: prev.subscribers.filter(d => !(d.period === period && d.year === year)),
      spending: prev.spending.filter(d => !(d.period === period && d.year === year))
    }));
  };

  // Export functions
  const exportCSV = () => {
    let csv = 'VASIA Dashboard Export\n\n';
    csv += 'REVENUE\nPeriod,Year,VIP Messages,VIP Tips,VIP Subs,VIP Total,FREE Messages,FREE Total,Overall\n';
    allData.revenue.forEach(d => {
      csv += `${d.period},${d.year},${d.vipMessages},${d.vipTips},${d.vipSubs},${d.vipTotal},${d.freeMessages},${d.freeTotal},${d.overall}\n`;
    });
    csv += '\nSUBSCRIBERS\nPeriod,Year,VIP New,VIP Active,VIP Renewed,VIP OF Stats,FREE New,FREE Active\n';
    allData.subscribers.forEach(d => {
      csv += `${d.period},${d.year},${d.vipNew},${d.vipActive},${d.vipRenewed},${d.vipOFStats},${d.freeNew},${d.freeActive}\n`;
    });
    csv += '\nAVERAGE SPEND\nPeriod,Year,VIP Per Spender,VIP Per Transaction,VIP New Sub Avg,VIP Active Fan Avg,FREE Per Spender\n';
    allData.spending.forEach(d => {
      csv += `${d.period},${d.year},${d.vipPerSpender},${d.vipPerTransaction},${d.vipNewSubAvg},${d.vipActiveFanAvg},${d.freePerSpender}\n`;
    });
    csv += '\nMARKETING - REDDIT\nPeriod,Year,Posts,Upvotes,Impressions,Avg Upvotes,Avg Impressions\n';
    allMarketingData.forEach(d => {
      csv += `${d.period},${d.year},${d.reddit.posts},${d.reddit.upvotes},${d.reddit.impressions},${d.reddit.avgUpvotes},${d.reddit.avgImpressions}\n`;
    });
    csv += '\nMARKETING - THREADS\nPeriod,Year,Posts,Followers Gained,Likes,Comments,Total Followers\n';
    allMarketingData.forEach(d => {
      csv += `${d.period},${d.year},${d.threads.posts},${d.threads.followersGained},${d.threads.likes},${d.threads.comments},${d.threads.totalFollowers}\n`;
    });
    csv += '\nMARKETING - REELS\nPeriod,Year,Posted,Followers Gained,Views,Likes,Comments,Avg Views,Total Followers\n';
    allMarketingData.filter(d => d.reels).forEach(d => {
      csv += `${d.period},${d.year},${d.reels.posted},${d.reels.followersGained},${d.reels.views},${d.reels.likes},${d.reels.comments},${d.reels.avgViews},${d.reels.totalFollowers}\n`;
    });
    csv += '\nACCOUNT STATUS\nPlatform,Account,Status,Stats Source,Account Count,Note\n';
    activeAccountRows.forEach(d => {
      csv += `"${d.platform}","${d.account}","${d.status}","${d.statsSource}","${d.accountCount || 1}","${d.note}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vasia-dashboard-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    const exportData = { ...allData, marketing: allMarketingData, accounts: activeAccountRows };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vasia-dashboard-backup.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Fill test data for add form
  const fillTestData = () => {
    setNewEntry({
      period: 'Test Week',
      year: '2026',
      vipMessages: '5000',
      vipTips: '800',
      vipSubs: '150',
      freeMessages: '0',
      vipNew: '300',
      vipActive: '500',
      freeActive: '0',
      vipPerSpender: '100'
    });
  };

  const filteredRevenue = getActiveData(allData.revenue);
  const filteredSubscribers = getActiveData(allData.subscribers);
  const filteredSpending = getActiveData(allData.spending);
  const filteredMarketing = getActiveMarketingData();

  // Get latest data for highlight cards
  const latestRevenue = filteredRevenue[filteredRevenue.length - 1];
  const prevRevenue = filteredRevenue[filteredRevenue.length - 2];
  const latestSubs = filteredSubscribers[filteredSubscribers.length - 1];
  const prevSubs = filteredSubscribers[filteredSubscribers.length - 2];
  const latestSpending = filteredSpending[filteredSpending.length - 1];
  const latestMarketing = filteredMarketing[filteredMarketing.length - 1];
  const prevMarketing = filteredMarketing[filteredMarketing.length - 2];
  const comparisonDataSource = activeTab === 'marketing' ? allMarketingData : allData.revenue;
  const comparisonPeriods = getAllPeriods(comparisonDataSource);
  const inviteToken = new URLSearchParams(window.location.search).get('invite');
  const isAdmin = profile?.role === 'admin' || profile?.role === 'internal';
  const isModel = profile?.role === 'model';
  const canManageData = isAdmin;
  const visibleTabs = useMemo(() => getVisibleTabsForProfile(profile), [profile]);
  const hideGlobalControls = activeTab === 'agencyAnalytics' || activeTab === 'adminAccess' || activeTab === 'reelInsights' || activeTab === 'aiReelCreator' || activeTab === 'dataConnections';
  const roleBadgeLabel = isAdmin ? 'Admin' : isModel ? 'Model' : 'Analytics viewer';
  const roleBadgeClass = isAdmin
    ? 'border-purple-500/40 bg-purple-500/10 text-purple-200'
    : isModel
      ? 'border-green-500/40 bg-green-500/10 text-green-200'
      : 'border-blue-500/40 bg-blue-500/10 text-blue-200';

  useEffect(() => {
    if (profile && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [profile, activeTab, visibleTabs]);

  useEffect(() => {
    if (profile && session?.access_token) refreshLiveMarketingData(session.access_token);
  }, [profile?.id, session?.access_token]);

  const handleAuthenticated = async ({ session: nextSession, profile: nextProfile, shouldLoadProfile }) => {
    if (nextSession) setSession(nextSession);
    if (nextProfile) {
      setProfile(nextProfile);
      return;
    }
    if (shouldLoadProfile && nextSession) {
      await hydrateProfile(nextSession);
    }
  };

  const handleSignOut = async () => {
    await signOut(session?.access_token);
    setSession(null);
    setProfile(null);
    setActiveTab('agencyAnalytics');
  };

  const latestRevenuePeriod = allData.revenue[allData.revenue.length - 1]?.period || 'Awaiting data';
  const dataModeLabel = analyticsDataStatus.source === 'live_api' ? 'API synced' : 'Historical data';

  const scrollTabs = (direction) => {
    tabStripRef.current?.scrollBy({
      behavior: 'smooth',
      left: direction * Math.min(460, window.innerWidth * 0.72)
    });
  };

  if (authLoading) {
    return (
      <div className="app-shell min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <div className="text-sm text-gray-400">Loading dashboard access...</div>
      </div>
    );
  }

  if (isSupabaseConfigured && !profile) {
    return <AuthScreen inviteToken={inviteToken} onAuthenticated={handleAuthenticated} />;
  }

  return (
    <div className="app-shell min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="dashboard-header bg-gray-900 border-b border-gray-800 px-6 py-4">
        <div className="dashboard-header-inner flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-between md:gap-0">
          <div className="creator-mini-brand">
            <span className="creator-mini-mark"><Gamepad2 size={19} /></span>
            <div>
              <div className="creator-eyebrow">VASIA // CREATOR OPS</div>
              <p className="creator-mini-copy">Performance command centre</p>
            </div>
          </div>
          <div className="dashboard-actions grid w-full grid-cols-2 gap-2 md:flex md:w-auto md:items-center md:gap-3">
            <span className={`hidden md:inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${roleBadgeClass}`}>
              <Shield size={13} />
              {roleBadgeLabel}
            </span>
            {canManageData && (
              <>
                <button onClick={exportCSV} className="min-w-0 flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors">
                  <Download size={16} /> CSV
                </button>
                <button onClick={exportJSON} className="min-w-0 flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors">
                  <Download size={16} /> JSON
                </button>
                <button onClick={() => setShowUpload(!showUpload)} className="min-w-0 flex items-center justify-center gap-2 px-3 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm transition-colors">
                  <Plus size={16} /> Add Week
                </button>
              </>
            )}
            {isSupabaseConfigured && (
              <button onClick={handleSignOut} className="min-w-0 flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors">
                <LogOut size={16} /> Sign out
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="tab-navigation mt-4">
          <button className="tab-scroll-control" onClick={() => scrollTabs(-1)} aria-label="Show earlier dashboard sections">
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
          <div ref={tabStripRef} className="tab-strip flex gap-1" aria-label="Dashboard sections">
            {visibleTabs.map(tab => {
              const TabIcon = TAB_ICONS[tab] || Gamepad2;
              return (
                <button
                  key={tab}
                  onClick={(event) => {
                    setActiveTab(tab);
                    event.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                  }}
                  style={{ order: DASHBOARD_TABS.findIndex((item) => item.key === tab) * 2 }}
                  className={`tab-button px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                    activeTab === tab
                      ? 'is-active bg-gray-950 text-white border-t-2 border-purple-500'
                      : 'bg-gray-800 text-gray-400 hover:text-gray-200'
                  }`}
                >
                  <TabIcon size={15} aria-hidden="true" />
                  <span>{getTabLabel(tab)}</span>
                </button>
              );
            })}
          </div>
          <button className="tab-scroll-control" onClick={() => scrollTabs(1)} aria-label="Show more dashboard sections">
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>
      </div>

      <section className="dashboard-hero-wrap" aria-labelledby="dashboard-hero-title">
        <div className="dashboard-hero">
          <div className="dashboard-hero-art" aria-hidden="true">
            <span className="hero-player-tag">PLAYER_01</span>
            <span className="hero-online-tag"><i className="hud-dot" /> COMMAND DECK ONLINE</span>
            <span className="hero-reticle" />
            <span className="hero-petal hero-petal-one" />
            <span className="hero-petal hero-petal-two" />
            <span className="hero-petal hero-petal-three" />
          </div>

          <div className="dashboard-hero-copy creator-brand-lockup">
            <div className="hero-overline"><Sparkles size={14} /> Sakura command deck</div>
            <h1 id="dashboard-hero-title" className="dashboard-title creator-title text-2xl font-bold">
              GG, Vasia. <span>Let&apos;s level up.</span>
            </h1>
            <p className="creator-subtitle text-sm text-gray-400 mt-1">
              Revenue, audience and content intelligence in one beautifully focused creator HQ.
            </p>
            <div className="creator-status-row" aria-label="Dashboard data status">
              <span><i className="hud-dot" /> {dataModeLabel}</span>
              <span><Radio size={12} /> Latest: {latestRevenuePeriod}</span>
              <span><Sparkles size={12} /> Cozy competitive mode</span>
            </div>

            <div className="hero-mission-grid" aria-label="Command deck snapshot">
              <div className="hero-mission-card hero-mission-primary">
                <span className="hero-mission-label">Current data drop</span>
                <strong>{latestRevenuePeriod}</strong>
              </div>
              <div className="hero-mission-card">
                <span className="hero-mission-label">Signal</span>
                <strong>{dataModeLabel}</strong>
              </div>
              <div className="hero-mission-card">
                <span className="hero-mission-label">Access</span>
                <strong>{roleBadgeLabel}</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="dashboard-content p-6">
        {!hideGlobalControls && (
          <>
        {/* Filters */}
        <div className="control-row flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
            >
              <option value="all">All Years</option>
              {getAvailableYears().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <select
            value={selectedWeeks}
            onChange={(e) => setSelectedWeeks(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
          >
            <option value="all">All Weeks</option>
            <option value="4">4 Weeks</option>
            <option value="8">8 Weeks</option>
            <option value="12">12 Weeks</option>
            <option value="26">26 Weeks</option>
          </select>
          {activeTab === 'revenue' && (
            <select
              value={selectedRevenueType}
              onChange={(e) => setSelectedRevenueType(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
            >
              <option value="total">All Revenue</option>
              <option value="messages">Messages Only</option>
              <option value="tips">Tips Only</option>
              <option value="subscriptions">Subscriptions Only</option>
            </select>
          )}
          {activeTab === 'marketing' && (
            <select
              value={selectedMarketingPlatform}
              onChange={(e) => setSelectedMarketingPlatform(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
            >
              <option value="all">All Platforms</option>
              <option value="x">X (Twitter)</option>
              <option value="reddit">Reddit</option>
              <option value="threads">Threads</option>
              <option value="reels">Reels</option>
            </select>
          )}
        </div>

        {/* Comparison Mode Toggle */}
        <div className="segmented-scroll flex flex-wrap items-center gap-3 mb-6">
          <span className="text-xs text-gray-500 uppercase tracking-wider mr-1">Mode:</span>
          {[
            { key: 'standard', label: 'Standard' },
            { key: 'compare', label: 'Compare Weeks' },
            { key: 'yearly', label: 'Year vs Year' },
            { key: 'range', label: 'Date Range' }
          ].map(m => (
            <button
              key={m.key}
              onClick={() => { setComparisonMode(m.key); setCompareWeekA(''); setCompareWeekB(''); setRangeStart(''); setRangeEnd(''); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                comparisonMode === m.key
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-gray-200 hover:bg-gray-700'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Compare Weeks Selectors */}
        {comparisonMode === 'compare' && (
          <div className="comparison-panel flex items-center gap-4 mb-6 p-4 bg-gray-900 rounded-xl border border-gray-800">
            <span className="text-sm text-gray-400">Week A:</span>
            <select value={compareWeekA} onChange={e => setCompareWeekA(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500">
              <option value="">Select week...</option>
              {comparisonPeriods.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <span className="text-sm text-gray-400">vs Week B:</span>
            <select value={compareWeekB} onChange={e => setCompareWeekB(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500">
              <option value="">Select week...</option>
              {comparisonPeriods.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        )}

        {/* Date Range Selectors */}
        {comparisonMode === 'range' && (
          <div className="comparison-panel flex items-center gap-4 mb-6 p-4 bg-gray-900 rounded-xl border border-gray-800">
            <span className="text-sm text-gray-400">From:</span>
            <select value={rangeStart} onChange={e => setRangeStart(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500">
              <option value="">Start week...</option>
              {comparisonPeriods.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <span className="text-sm text-gray-400">To:</span>
            <select value={rangeEnd} onChange={e => setRangeEnd(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500">
              <option value="">End week...</option>
              {comparisonPeriods.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            {rangeStart && rangeEnd && (
              <span className="text-xs text-purple-400">
                {getRangeData(comparisonDataSource, rangeStart, rangeEnd).length} weeks selected
              </span>
            )}
          </div>
        )}
          </>
        )}

        {/* Add New Week Panel */}
        {canManageData && showUpload && (
          <div className="mobile-card bg-gray-900 border border-gray-700 rounded-xl p-6 mb-6">
            <div className="mobile-panel-header flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add New Week Data</h3>
              <div className="flex gap-2">
                <button onClick={fillTestData} className="text-xs px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded transition-colors">
                  Fill Test Data
                </button>
                <button onClick={() => setShowUpload(false)} className="text-gray-400 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Period</label>
                <input type="text" placeholder="e.g. Feb 23-Mar 1" value={newEntry.period} onChange={(e) => setNewEntry({ ...newEntry, period: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Year</label>
                <select value={newEntry.year} onChange={(e) => setNewEntry({ ...newEntry, year: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500">
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">VIP Messages $</label>
                <input type="number" value={newEntry.vipMessages} onChange={(e) => setNewEntry({ ...newEntry, vipMessages: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">VIP Tips $</label>
                <input type="number" value={newEntry.vipTips} onChange={(e) => setNewEntry({ ...newEntry, vipTips: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">VIP Subs $</label>
                <input type="number" value={newEntry.vipSubs} onChange={(e) => setNewEntry({ ...newEntry, vipSubs: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">FREE Messages $</label>
                <input type="number" value={newEntry.freeMessages} onChange={(e) => setNewEntry({ ...newEntry, freeMessages: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">VIP New Subs</label>
                <input type="number" value={newEntry.vipNew} onChange={(e) => setNewEntry({ ...newEntry, vipNew: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">VIP Active Subs</label>
                <input type="number" value={newEntry.vipActive} onChange={(e) => setNewEntry({ ...newEntry, vipActive: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">FREE Active Subs</label>
                <input type="number" value={newEntry.freeActive} onChange={(e) => setNewEntry({ ...newEntry, freeActive: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">VIP Avg per Spender</label>
                <input type="number" value={newEntry.vipPerSpender} onChange={(e) => setNewEntry({ ...newEntry, vipPerSpender: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-600 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500" />
              </div>
            </div>
            <button onClick={handleAddWeek} disabled={!newEntry.period}
              className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 rounded-lg text-sm font-medium transition-colors">
              Add Week
            </button>
          </div>
        )}

        {activeTab === 'agencyAnalytics' && (
          <AgencyAnalytics allData={allData} allMarketingData={allMarketingData} analyticsDataStatus={analyticsDataStatus} />
        )}

        {activeTab === 'adminAccess' && canManageData && (
          <AdminAccessPanel session={session} onCurrentUserUpdated={setProfile} />
        )}

        {activeTab === 'reelInsights' && (
          <ReelInsightsSection session={session} profile={profile} dashboardId="vasia" />
        )}

        {activeTab === 'aiReelCreator' && (
          <AIReelCreator session={session} dashboardId="vasia" />
        )}

        {activeTab === 'dataConnections' && (
          <SocialConnections session={session} profile={profile} dashboardId="vasia" onDataUpdated={refreshLiveMarketingData} />
        )}

        {/* ============ REVENUE TAB ============ */}
        {activeTab === 'revenue' && (
          <div>
            {/* --- COMPARE WEEKS MODE --- */}
            {comparisonMode === 'compare' && compareWeekA && compareWeekB ? (() => {
              const wA = getWeekByPeriod(allData.revenue, compareWeekA);
              const wB = getWeekByPeriod(allData.revenue, compareWeekB);
              if (!wA || !wB) return <p className="text-gray-400">Select two valid weeks to compare.</p>;
              const metrics = [
                { label: 'Overall Revenue', key: 'overall', prefix: '$' },
                { label: 'VIP Total', key: 'vipTotal', prefix: '$' },
                { label: 'VIP Messages', key: 'vipMessages', prefix: '$' },
                { label: 'VIP Tips', key: 'vipTips', prefix: '$' },
                { label: 'VIP Subs', key: 'vipSubs', prefix: '$' },
                { label: 'FREE Total', key: 'freeTotal', prefix: '$' }
              ];
              return (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {metrics.map(m => {
                      const cmp = getComparisonDiff(wA[m.key], wB[m.key]);
                      return (
                        <div key={m.key} className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                          <p className="text-sm text-gray-400 mb-2">{m.label}</p>
                          <div className="flex justify-between items-end">
                            <div>
                              <p className="text-xs text-gray-500">{wA.period}</p>
                              <p className="text-lg font-bold text-white">{m.prefix}{wA[m.key]?.toLocaleString()}</p>
                            </div>
                            <div className="text-center px-3">
                              <p className={`text-sm font-semibold ${cmp.direction === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                                {cmp.direction === 'up' ? '+' : ''}{cmp.pctChange.toFixed(1)}%
                              </p>
                              <p className={`text-xs ${cmp.direction === 'up' ? 'text-green-400/70' : 'text-red-400/70'}`}>
                                {cmp.direction === 'up' ? '+' : ''}{m.prefix}{cmp.diff.toLocaleString()}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">{wB.period}</p>
                              <p className="text-lg font-bold text-white">{m.prefix}{wB[m.key]?.toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                    <h3 className="text-lg font-semibold mb-4">Week Comparison</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={metrics.map(m => ({ metric: m.label, [wA.period]: wA[m.key], [wB.period]: wB[m.key] }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="metric" stroke="#9CA3AF" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                          formatter={(value) => '$' + value.toLocaleString()} />
                        <Legend />
                        <Bar dataKey={wA.period} fill="#A78BFA" radius={[2, 2, 0, 0]} />
                        <Bar dataKey={wB.period} fill="#34D399" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              );
            })()

            /* --- YEAR VS YEAR MODE --- */
            : comparisonMode === 'yearly' ? (() => {
              const fields = ['overall', 'vipTotal', 'freeTotal', 'vipMessages', 'vipTips', 'vipSubs'];
              const y25 = getYearAggregates(allData.revenue, '2025', fields);
              const y26 = getYearAggregates(allData.revenue, '2026', fields);
              const rows = [
                { label: 'Total Revenue', key: 'overall', prefix: '$' },
                { label: 'VIP Total', key: 'vipTotal', prefix: '$' },
                { label: 'VIP Messages', key: 'vipMessages', prefix: '$' },
                { label: 'VIP Tips', key: 'vipTips', prefix: '$' },
                { label: 'VIP Subs Revenue', key: 'vipSubs', prefix: '$' }
              ];
              return (
                <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                  <div className="p-4 border-b border-gray-800">
                    <h3 className="text-lg font-semibold">Revenue: Year vs Year</h3>
                    <p className="text-xs text-gray-500 mt-1">2025 ({y25.count} weeks) vs 2026 ({y26.count} weeks)</p>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left px-4 py-3 text-gray-400">Metric</th>
                        <th className="text-right px-4 py-3 text-gray-400">2025 Total</th>
                        <th className="text-right px-4 py-3 text-gray-400">2025 Wk Avg</th>
                        <th className="text-right px-4 py-3 text-gray-400">2026 Total</th>
                        <th className="text-right px-4 py-3 text-gray-400">2026 Wk Avg</th>
                        <th className="text-right px-4 py-3 text-gray-400">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => {
                        const avg25 = y25.count ? y25[r.key] / y25.count : 0;
                        const avg26 = y26.count ? y26[r.key] / y26.count : 0;
                        const cmp = getComparisonDiff(avg25, avg26);
                        return (
                          <tr key={r.key} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                            <td className="px-4 py-3 font-medium">{r.label}</td>
                            <td className="text-right px-4 py-3">{r.prefix}{y25[r.key]?.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                            <td className="text-right px-4 py-3 text-gray-400">{r.prefix}{avg25.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                            <td className="text-right px-4 py-3">{r.prefix}{y26[r.key]?.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                            <td className="text-right px-4 py-3 text-gray-400">{r.prefix}{avg26.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                            <td className={`text-right px-4 py-3 font-semibold ${cmp.direction === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                              {cmp.direction === 'up' ? '+' : ''}{cmp.pctChange.toFixed(1)}% avg
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()

            /* --- STANDARD & RANGE MODES --- */
            : (
              <>
                {/* Range summary */}
                {comparisonMode === 'range' && rangeStart && rangeEnd && filteredRevenue.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-900 rounded-xl p-5 border border-purple-800/50">
                      <p className="text-sm text-gray-400">Range Total Revenue</p>
                      <p className="text-2xl font-bold text-green-400">
                        ${filteredRevenue.reduce((s, d) => s + d.overall, 0).toLocaleString(undefined, {maximumFractionDigits: 2})}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{filteredRevenue.length} weeks</p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-purple-800/50">
                      <p className="text-sm text-gray-400">Range Avg / Week</p>
                      <p className="text-2xl font-bold text-purple-400">
                        ${(filteredRevenue.reduce((s, d) => s + d.overall, 0) / filteredRevenue.length).toLocaleString(undefined, {maximumFractionDigits: 2})}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Weekly average</p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-purple-800/50">
                      <p className="text-sm text-gray-400">Range Peak</p>
                      <p className="text-2xl font-bold text-yellow-400">
                        ${Math.max(...filteredRevenue.map(d => d.overall)).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {filteredRevenue.find(d => d.overall === Math.max(...filteredRevenue.map(x => x.overall)))?.period}
                      </p>
                    </div>
                  </div>
                )}

                {/* Standard highlights */}
                {comparisonMode === 'standard' && latestRevenue && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                      <p className="text-sm text-gray-400">Latest Week Total</p>
                      <p className="text-2xl font-bold text-green-400">${latestRevenue.overall?.toLocaleString()}</p>
                      <p className="text-xs text-gray-500 mt-1">{latestRevenue.period}</p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                      <p className="text-sm text-gray-400">VIP Contribution</p>
                      <p className="text-2xl font-bold text-purple-400">${latestRevenue.vipTotal?.toLocaleString()}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {latestRevenue.overall > 0 ? ((latestRevenue.vipTotal / latestRevenue.overall) * 100).toFixed(1) : 0}% of total
                      </p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                      <p className="text-sm text-gray-400">Growth vs Previous</p>
                      <p className={`text-2xl font-bold ${prevRevenue && latestRevenue.overall > prevRevenue.overall ? 'text-green-400' : 'text-red-400'}`}>
                        {prevRevenue ? getPercentChange(latestRevenue.overall, prevRevenue.overall) : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Week over week</p>
                    </div>
                  </div>
                )}

                {/* Chart */}
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6">
                  <h3 className="text-lg font-semibold mb-4">Revenue Trend</h3>
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={getRevenueChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="period" stroke="#9CA3AF" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />
                      <Legend />
                      {selectedRevenueType === 'total' && (
                        <>
                          <Line type="monotone" dataKey="Overall" stroke="#A78BFA" strokeWidth={2} dot={{ fill: '#A78BFA', r: 3 }} />
                          <Line type="monotone" dataKey="VIP Total" stroke="#34D399" strokeWidth={2} dot={{ fill: '#34D399', r: 3 }} />
                          <Line type="monotone" dataKey="FREE Total" stroke="#60A5FA" strokeWidth={1} dot={{ fill: '#60A5FA', r: 2 }} />
                        </>
                      )}
                      {selectedRevenueType === 'messages' && (
                        <>
                          <Line type="monotone" dataKey="VIP Messages" stroke="#34D399" strokeWidth={2} dot={{ fill: '#34D399', r: 3 }} />
                          <Line type="monotone" dataKey="FREE Messages" stroke="#60A5FA" strokeWidth={1} dot={{ fill: '#60A5FA', r: 2 }} />
                        </>
                      )}
                      {selectedRevenueType === 'tips' && (
                        <>
                          <Line type="monotone" dataKey="VIP Tips" stroke="#F59E0B" strokeWidth={2} dot={{ fill: '#F59E0B', r: 3 }} />
                          <Line type="monotone" dataKey="FREE Tips" stroke="#60A5FA" strokeWidth={1} dot={{ fill: '#60A5FA', r: 2 }} />
                        </>
                      )}
                      {selectedRevenueType === 'subscriptions' && (
                        <>
                          <Line type="monotone" dataKey="VIP Subs" stroke="#EC4899" strokeWidth={2} dot={{ fill: '#EC4899', r: 3 }} />
                          <Line type="monotone" dataKey="FREE Subs" stroke="#60A5FA" strokeWidth={1} dot={{ fill: '#60A5FA', r: 2 }} />
                        </>
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Table */}
                <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-800">
                          <th className="text-left px-4 py-3 text-gray-400 font-medium">Period</th>
                          {selectedRevenueType === 'total' && (
                            <>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">VIP Total</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">FREE Total</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Overall</th>
                            </>
                          )}
                          {selectedRevenueType === 'messages' && (
                            <>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">VIP Messages</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">FREE Messages</th>
                            </>
                          )}
                          {selectedRevenueType === 'tips' && (
                            <>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">VIP Tips</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">FREE Tips</th>
                            </>
                          )}
                          {selectedRevenueType === 'subscriptions' && (
                            <>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">VIP Subs</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">FREE Subs</th>
                            </>
                          )}
                          <th className="text-right px-4 py-3 text-gray-400 font-medium">% Change</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRevenue.map((d, i) => {
                          const prev = filteredRevenue[i - 1];
                          return (
                            <tr key={d.period + d.year} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                              <td className="px-4 py-3 font-medium">{d.period}</td>
                              {selectedRevenueType === 'total' && (
                                <>
                                  <td className="text-right px-4 py-3 text-green-400">${d.vipTotal?.toLocaleString()}</td>
                                  <td className="text-right px-4 py-3 text-blue-400">${d.freeTotal?.toLocaleString()}</td>
                                  <td className="text-right px-4 py-3 text-purple-400 font-semibold">${d.overall?.toLocaleString()}</td>
                                </>
                              )}
                              {selectedRevenueType === 'messages' && (
                                <>
                                  <td className="text-right px-4 py-3 text-green-400">${d.vipMessages?.toLocaleString()}</td>
                                  <td className="text-right px-4 py-3 text-blue-400">${d.freeMessages?.toLocaleString()}</td>
                                </>
                              )}
                              {selectedRevenueType === 'tips' && (
                                <>
                                  <td className="text-right px-4 py-3 text-yellow-400">${d.vipTips?.toLocaleString()}</td>
                                  <td className="text-right px-4 py-3 text-blue-400">${d.freeTips?.toLocaleString()}</td>
                                </>
                              )}
                              {selectedRevenueType === 'subscriptions' && (
                                <>
                                  <td className="text-right px-4 py-3 text-pink-400">${d.vipSubs?.toLocaleString()}</td>
                                  <td className="text-right px-4 py-3 text-blue-400">${d.freeSubs?.toLocaleString()}</td>
                                </>
                              )}
                              <td className={`text-right px-4 py-3 ${prev && d.overall > prev.overall ? 'text-green-400' : 'text-red-400'}`}>
                                {prev ? getPercentChange(d.overall, prev.overall) : '-'}
                              </td>
                              <td className="text-right px-4 py-3">
                                <button onClick={() => handleDeleteWeek(d.period, d.year)} className="text-gray-500 hover:text-red-400 transition-colors">
                                  <Trash2 size={14} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ============ SUBSCRIBERS TAB ============ */}
        {activeTab === 'subscribers' && (
          <div>
            {/* --- COMPARE WEEKS MODE --- */}
            {comparisonMode === 'compare' && compareWeekA && compareWeekB ? (() => {
              const wA = getWeekByPeriod(allData.subscribers, compareWeekA);
              const wB = getWeekByPeriod(allData.subscribers, compareWeekB);
              if (!wA || !wB) return <p className="text-gray-400">Select two valid weeks to compare.</p>;
              const metrics = [
                { label: 'VIP Active', key: 'vipActive' },
                { label: 'VIP New', key: 'vipNew' },
                { label: 'VIP Renewed', key: 'vipRenewed' },
                { label: 'OF Stats', key: 'vipOFStats' },
                { label: 'OF New+Renew', key: 'vipOFSNewRenew' }
              ];
              return (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {metrics.map(m => {
                      const cmp = getComparisonDiff(wA[m.key], wB[m.key]);
                      return (
                        <div key={m.key} className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                          <p className="text-sm text-gray-400 mb-2">{m.label}</p>
                          <div className="flex justify-between items-end">
                            <div>
                              <p className="text-xs text-gray-500">{wA.period}</p>
                              <p className="text-lg font-bold text-white">{wA[m.key]?.toLocaleString()}</p>
                            </div>
                            <div className="text-center px-3">
                              <p className={`text-sm font-semibold ${cmp.direction === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                                {cmp.direction === 'up' ? '+' : ''}{cmp.pctChange.toFixed(1)}%
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">{wB.period}</p>
                              <p className="text-lg font-bold text-white">{wB[m.key]?.toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                    <h3 className="text-lg font-semibold mb-4">Week Comparison</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={metrics.map(m => ({ metric: m.label, [wA.period]: wA[m.key], [wB.period]: wB[m.key] }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="metric" stroke="#9CA3AF" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />
                        <Legend />
                        <Bar dataKey={wA.period} fill="#A78BFA" radius={[2, 2, 0, 0]} />
                        <Bar dataKey={wB.period} fill="#34D399" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              );
            })()

            /* --- YEAR VS YEAR MODE --- */
            : comparisonMode === 'yearly' ? (() => {
              const fields = ['vipActive', 'vipNew', 'vipRenewed', 'vipOFStats', 'vipOFSNewRenew'];
              const y25 = getYearAggregates(allData.subscribers, '2025', fields);
              const y26 = getYearAggregates(allData.subscribers, '2026', fields);
              const rows = [
                { label: 'Total VIP New', key: 'vipNew' },
                { label: 'Avg VIP Active / Week', key: 'vipActive', useAvg: true },
                { label: 'Total VIP Renewed', key: 'vipRenewed' },
                { label: 'Total OF Stats', key: 'vipOFStats' },
                { label: 'Total OF New+Renew', key: 'vipOFSNewRenew' }
              ];
              return (
                <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                  <div className="p-4 border-b border-gray-800">
                    <h3 className="text-lg font-semibold">Subscribers: Year vs Year</h3>
                    <p className="text-xs text-gray-500 mt-1">2025 ({y25.count} weeks) vs 2026 ({y26.count} weeks)</p>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left px-4 py-3 text-gray-400">Metric</th>
                        <th className="text-right px-4 py-3 text-gray-400">2025</th>
                        <th className="text-right px-4 py-3 text-gray-400">2026</th>
                        <th className="text-right px-4 py-3 text-gray-400">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => {
                        const v25 = r.useAvg ? (y25.count ? y25[r.key] / y25.count : 0) : y25[r.key];
                        const v26 = r.useAvg ? (y26.count ? y26[r.key] / y26.count : 0) : y26[r.key];
                        const cmp = getComparisonDiff(v25, v26);
                        return (
                          <tr key={r.key} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                            <td className="px-4 py-3 font-medium">{r.label}</td>
                            <td className="text-right px-4 py-3">{r.useAvg ? v25.toFixed(0) : v25.toLocaleString()}</td>
                            <td className="text-right px-4 py-3">{r.useAvg ? v26.toFixed(0) : v26.toLocaleString()}</td>
                            <td className={`text-right px-4 py-3 font-semibold ${cmp.direction === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                              {cmp.direction === 'up' ? '+' : ''}{cmp.pctChange.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()

            /* --- STANDARD & RANGE MODES --- */
            : (
              <>
                {comparisonMode === 'range' && rangeStart && rangeEnd && filteredSubscribers.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-900 rounded-xl p-5 border border-purple-800/50">
                      <p className="text-sm text-gray-400">Range Total New VIP</p>
                      <p className="text-2xl font-bold text-green-400">{filteredSubscribers.reduce((s, d) => s + d.vipNew, 0).toLocaleString()}</p>
                      <p className="text-xs text-gray-500 mt-1">{filteredSubscribers.length} weeks</p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-purple-800/50">
                      <p className="text-sm text-gray-400">Avg Active / Week</p>
                      <p className="text-2xl font-bold text-purple-400">{(filteredSubscribers.reduce((s, d) => s + d.vipActive, 0) / filteredSubscribers.length).toFixed(0)}</p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-purple-800/50">
                      <p className="text-sm text-gray-400">Peak Active</p>
                      <p className="text-2xl font-bold text-yellow-400">{Math.max(...filteredSubscribers.map(d => d.vipActive)).toLocaleString()}</p>
                    </div>
                  </div>
                )}

                {comparisonMode === 'standard' && latestSubs && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                      <p className="text-sm text-gray-400">VIP Active Subs</p>
                      <p className="text-2xl font-bold text-purple-400">{latestSubs.vipActive?.toLocaleString()}</p>
                      <p className="text-xs text-gray-500 mt-1">{latestSubs.period}</p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                      <p className="text-sm text-gray-400">VIP New This Week</p>
                      <p className="text-2xl font-bold text-green-400">{latestSubs.vipNew?.toLocaleString()}</p>
                      <p className="text-xs text-gray-500 mt-1">Creators Dashboard</p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                      <p className="text-sm text-gray-400">VIP Growth</p>
                      <p className={`text-2xl font-bold ${prevSubs && latestSubs.vipActive > prevSubs.vipActive ? 'text-green-400' : 'text-red-400'}`}>
                        {prevSubs ? getPercentChange(latestSubs.vipActive, prevSubs.vipActive) : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Week over week</p>
                    </div>
                  </div>
                )}

                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6">
                  <h3 className="text-lg font-semibold mb-4">Subscriber Trend</h3>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={filteredSubscribers.map(d => ({ period: d.period, 'VIP Active': d.vipActive, 'VIP New': d.vipNew }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="period" stroke="#9CA3AF" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />
                      <Legend />
                      <Bar dataKey="VIP Active" fill="#A78BFA" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="VIP New" fill="#34D399" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-800">
                          <th className="text-left px-4 py-3 text-gray-400 font-medium">Period</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium">VIP New</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium">VIP Active</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium">VIP Renewed</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium">OF Stats</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium">FREE Active</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSubscribers.map((d) => (
                          <tr key={d.period + d.year} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                            <td className="px-4 py-3 font-medium">{d.period}</td>
                            <td className="text-right px-4 py-3 text-green-400">{d.vipNew}</td>
                            <td className="text-right px-4 py-3 text-purple-400">{d.vipActive}</td>
                            <td className="text-right px-4 py-3 text-yellow-400">{d.vipRenewed}</td>
                            <td className="text-right px-4 py-3 text-blue-400">{d.vipOFStats}</td>
                            <td className="text-right px-4 py-3 text-gray-400">{d.freeActive || '-'}</td>
                            <td className="text-right px-4 py-3">
                              <button onClick={() => handleDeleteWeek(d.period, d.year)} className="text-gray-500 hover:text-red-400 transition-colors">
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ============ SPENDING TAB ============ */}
        {activeTab === 'spending' && (
          <div>
            {/* --- COMPARE WEEKS MODE --- */}
            {comparisonMode === 'compare' && compareWeekA && compareWeekB ? (() => {
              const wA = getWeekByPeriod(allData.spending, compareWeekA);
              const wB = getWeekByPeriod(allData.spending, compareWeekB);
              if (!wA || !wB) return <p className="text-gray-400">Select two valid weeks to compare.</p>;
              const metrics = [
                { label: 'VIP per Spender', key: 'vipPerSpender', prefix: '$' },
                { label: 'VIP per Transaction', key: 'vipPerTransaction', prefix: '$' },
                { label: 'VIP New Sub Avg', key: 'vipNewSubAvg', prefix: '$' },
                { label: 'VIP Active Fan Avg', key: 'vipActiveFanAvg', prefix: '$' }
              ];
              return (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {metrics.map(m => {
                      const cmp = getComparisonDiff(wA[m.key], wB[m.key]);
                      return (
                        <div key={m.key} className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                          <p className="text-sm text-gray-400 mb-2">{m.label}</p>
                          <div className="flex justify-between items-end">
                            <div>
                              <p className="text-xs text-gray-500">{wA.period}</p>
                              <p className="text-lg font-bold text-white">{m.prefix}{wA[m.key]}</p>
                            </div>
                            <div className="text-center px-3">
                              <p className={`text-sm font-semibold ${cmp.direction === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                                {cmp.direction === 'up' ? '+' : ''}{cmp.pctChange.toFixed(1)}%
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">{wB.period}</p>
                              <p className="text-lg font-bold text-white">{m.prefix}{wB[m.key]}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                    <h3 className="text-lg font-semibold mb-4">Week Comparison</h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={metrics.map(m => ({ metric: m.label, [wA.period]: wA[m.key], [wB.period]: wB[m.key] }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="metric" stroke="#9CA3AF" tick={{ fontSize: 11 }} />
                        <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                        <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                          formatter={(value) => '$' + value} />
                        <Legend />
                        <Bar dataKey={wA.period} fill="#A78BFA" radius={[2, 2, 0, 0]} />
                        <Bar dataKey={wB.period} fill="#34D399" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              );
            })()

            /* --- YEAR VS YEAR MODE --- */
            : comparisonMode === 'yearly' ? (() => {
              const fields = ['vipPerSpender', 'vipPerTransaction', 'vipNewSubAvg', 'vipActiveFanAvg'];
              const y25 = getYearAggregates(allData.spending, '2025', fields, 'avg');
              const y26 = getYearAggregates(allData.spending, '2026', fields, 'avg');
              const rows = [
                { label: 'Avg VIP per Spender', key: 'vipPerSpender' },
                { label: 'Avg VIP per Transaction', key: 'vipPerTransaction' },
                { label: 'Avg VIP New Sub Avg', key: 'vipNewSubAvg' },
                { label: 'Avg VIP Active Fan Avg', key: 'vipActiveFanAvg' }
              ];
              return (
                <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                  <div className="p-4 border-b border-gray-800">
                    <h3 className="text-lg font-semibold">Avg Spend: Year vs Year</h3>
                    <p className="text-xs text-gray-500 mt-1">2025 ({y25.count} weeks) vs 2026 ({y26.count} weeks) — averaged across all weeks</p>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left px-4 py-3 text-gray-400">Metric</th>
                        <th className="text-right px-4 py-3 text-gray-400">2025 Avg</th>
                        <th className="text-right px-4 py-3 text-gray-400">2026 Avg</th>
                        <th className="text-right px-4 py-3 text-gray-400">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => {
                        const cmp = getComparisonDiff(y25[r.key], y26[r.key]);
                        return (
                          <tr key={r.key} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                            <td className="px-4 py-3 font-medium">{r.label}</td>
                            <td className="text-right px-4 py-3">${y25[r.key]?.toFixed(2)}</td>
                            <td className="text-right px-4 py-3">${y26[r.key]?.toFixed(2)}</td>
                            <td className={`text-right px-4 py-3 font-semibold ${cmp.direction === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                              {cmp.direction === 'up' ? '+' : ''}{cmp.pctChange.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()

            /* --- STANDARD & RANGE MODES --- */
            : (
              <>
                {comparisonMode === 'range' && rangeStart && rangeEnd && filteredSpending.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-900 rounded-xl p-5 border border-purple-800/50">
                      <p className="text-sm text-gray-400">Range Avg per Spender</p>
                      <p className="text-2xl font-bold text-green-400">
                        ${(filteredSpending.reduce((s, d) => s + d.vipPerSpender, 0) / filteredSpending.length).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{filteredSpending.length} weeks</p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-purple-800/50">
                      <p className="text-sm text-gray-400">Range Avg per Transaction</p>
                      <p className="text-2xl font-bold text-purple-400">
                        ${(filteredSpending.reduce((s, d) => s + d.vipPerTransaction, 0) / filteredSpending.length).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-purple-800/50">
                      <p className="text-sm text-gray-400">Peak per Spender</p>
                      <p className="text-2xl font-bold text-yellow-400">
                        ${Math.max(...filteredSpending.map(d => d.vipPerSpender)).toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}

                {comparisonMode === 'standard' && latestSpending && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                      <p className="text-sm text-gray-400">VIP Avg per Spender</p>
                      <p className="text-2xl font-bold text-green-400">${latestSpending.vipPerSpender}</p>
                      <p className="text-xs text-gray-500 mt-1">{latestSpending.period}</p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                      <p className="text-sm text-gray-400">VIP New Sub Avg</p>
                      <p className="text-2xl font-bold text-purple-400">${latestSpending.vipNewSubAvg}</p>
                      <p className="text-xs text-gray-500 mt-1">New subscriber average spend</p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                      <p className="text-sm text-gray-400">VIP Active Fan Avg</p>
                      <p className="text-2xl font-bold text-blue-400">${latestSpending.vipActiveFanAvg}</p>
                      <p className="text-xs text-gray-500 mt-1">Active fan average spend</p>
                    </div>
                  </div>
                )}

                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6">
                  <h3 className="text-lg font-semibold mb-4">Average Spend Trend</h3>
                  <ResponsiveContainer width="100%" height={350}>
                    <LineChart data={filteredSpending.map(d => ({ period: d.period, 'VIP per Spender': d.vipPerSpender, 'VIP New Sub Avg': d.vipNewSubAvg }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="period" stroke="#9CA3AF" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }} />
                      <Legend />
                      <Line type="monotone" dataKey="VIP per Spender" stroke="#34D399" strokeWidth={2} dot={{ fill: '#34D399', r: 3 }} />
                      <Line type="monotone" dataKey="VIP New Sub Avg" stroke="#A78BFA" strokeWidth={2} dot={{ fill: '#A78BFA', r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-800">
                          <th className="text-left px-4 py-3 text-gray-400 font-medium">Period</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium">VIP/Spender</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium">VIP/Transaction</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium">VIP New Sub Avg</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium">VIP Active Fan</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium">FREE/Spender</th>
                          <th className="text-right px-4 py-3 text-gray-400 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSpending.map((d) => (
                          <tr key={d.period + d.year} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                            <td className="px-4 py-3 font-medium">{d.period}</td>
                            <td className="text-right px-4 py-3 text-green-400">${d.vipPerSpender}</td>
                            <td className="text-right px-4 py-3 text-yellow-400">${d.vipPerTransaction}</td>
                            <td className="text-right px-4 py-3 text-purple-400">${d.vipNewSubAvg}</td>
                            <td className="text-right px-4 py-3 text-blue-400">${d.vipActiveFanAvg}</td>
                            <td className="text-right px-4 py-3 text-gray-400">{d.freePerSpender > 0 ? `$${d.freePerSpender}` : '-'}</td>
                            <td className="text-right px-4 py-3">
                              <button onClick={() => handleDeleteWeek(d.period, d.year)} className="text-gray-500 hover:text-red-400 transition-colors">
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ============ MARKETING TAB ============ */}
        {activeTab === 'marketing' && (
          <div>
            <SocialAnalyticsDrilldown session={session} dashboardId="vasia" onOpenConnections={() => setActiveTab('dataConnections')} />
            {/* --- COMPARE WEEKS MODE --- */}
            {comparisonMode === 'compare' && compareWeekA && compareWeekB ? (() => {
              const wA = allMarketingData.find(d => d.period === compareWeekA);
              const wB = allMarketingData.find(d => d.period === compareWeekB);
              if (!wA || !wB) return <p className="text-gray-400">Select two valid marketing weeks to compare. Note: marketing data may not cover all weeks.</p>;
              const redditMetrics = [
                { label: 'Posts', get: d => d.reddit.posts },
                { label: 'Upvotes', get: d => d.reddit.upvotes },
                { label: 'Impressions', get: d => d.reddit.impressions }
              ];
              const threadsMetrics = [
                { label: 'Followers Gained', get: d => d.threads.followersGained },
                { label: 'Likes', get: d => d.threads.likes },
                { label: 'Total Followers', get: d => d.threads.totalFollowers }
              ];
              const reelsMetrics = wA.reels && wB.reels ? [
                { label: 'Views', get: d => d.reels.views },
                { label: 'Likes', get: d => d.reels.likes },
                { label: 'Followers Gained', get: d => d.reels.followersGained }
              ] : [];
              const renderCompareSection = (title, color, metrics) => (
                <div className="mb-6">
                  <h3 className={`text-lg font-semibold mb-3 ${color}`}>{title}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {metrics.map(m => {
                      const vA = m.get(wA);
                      const vB = m.get(wB);
                      const cmp = getComparisonDiff(vA, vB);
                      return (
                        <div key={m.label} className="bg-gray-900 rounded-xl p-5 border border-gray-800">
                          <p className="text-sm text-gray-400 mb-2">{m.label}</p>
                          <div className="flex justify-between items-end">
                            <div>
                              <p className="text-xs text-gray-500">{wA.period}</p>
                              <p className="text-lg font-bold text-white">{vA?.toLocaleString()}</p>
                            </div>
                            <p className={`text-sm font-semibold ${cmp.direction === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                              {cmp.direction === 'up' ? '+' : ''}{cmp.pctChange.toFixed(1)}%
                            </p>
                            <div className="text-right">
                              <p className="text-xs text-gray-500">{wB.period}</p>
                              <p className="text-lg font-bold text-white">{vB?.toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
              return (
                <>
                  {renderCompareSection('Reddit', 'text-orange-400', redditMetrics)}
                  {renderCompareSection('Threads', 'text-blue-400', threadsMetrics)}
                  {reelsMetrics.length > 0 && renderCompareSection('Reels', 'text-pink-400', reelsMetrics)}
                  {reelsMetrics.length === 0 && <p className="text-sm text-gray-500 mb-6">Reels data not available for both selected weeks.</p>}
                </>
              );
            })()

            /* --- YEAR VS YEAR MODE --- */
            : comparisonMode === 'yearly' ? (() => {
              const m25 = allMarketingData.filter(d => d.year === '2025');
              const m26 = allMarketingData.filter(d => d.year === '2026');
              const sumField = (arr, platform, field) => arr.reduce((s, d) => s + (d[platform]?.[field] || 0), 0);
              const avgField = (arr, platform, field) => arr.length ? sumField(arr, platform, field) / arr.length : 0;
              const rows = [
                { label: 'Reddit Total Posts', v25: sumField(m25, 'reddit', 'posts'), v26: sumField(m26, 'reddit', 'posts') },
                { label: 'Reddit Total Upvotes', v25: sumField(m25, 'reddit', 'upvotes'), v26: sumField(m26, 'reddit', 'upvotes') },
                { label: 'Reddit Total Impressions', v25: sumField(m25, 'reddit', 'impressions'), v26: sumField(m26, 'reddit', 'impressions') },
                { label: 'Reddit Avg Impressions/Wk', v25: avgField(m25, 'reddit', 'impressions'), v26: avgField(m26, 'reddit', 'impressions') },
                { label: 'Threads Total Followers Gained', v25: sumField(m25, 'threads', 'followersGained'), v26: sumField(m26, 'threads', 'followersGained') },
                { label: 'Threads Total Likes', v25: sumField(m25, 'threads', 'likes'), v26: sumField(m26, 'threads', 'likes') },
                { label: 'Reels Total Views', v25: m25.filter(d => d.reels).reduce((s, d) => s + d.reels.views, 0), v26: m26.filter(d => d.reels).reduce((s, d) => s + d.reels.views, 0) },
                { label: 'Reels Total Likes', v25: m25.filter(d => d.reels).reduce((s, d) => s + d.reels.likes, 0), v26: m26.filter(d => d.reels).reduce((s, d) => s + d.reels.likes, 0) }
              ];
              return (
                <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                  <div className="p-4 border-b border-gray-800">
                    <h3 className="text-lg font-semibold">Marketing: Year vs Year</h3>
                    <p className="text-xs text-gray-500 mt-1">2025 ({m25.length} weeks) vs 2026 ({m26.length} weeks)</p>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-800">
                        <th className="text-left px-4 py-3 text-gray-400">Metric</th>
                        <th className="text-right px-4 py-3 text-gray-400">2025</th>
                        <th className="text-right px-4 py-3 text-gray-400">2026</th>
                        <th className="text-right px-4 py-3 text-gray-400">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => {
                        const cmp = getComparisonDiff(r.v25, r.v26);
                        return (
                          <tr key={r.label} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                            <td className="px-4 py-3 font-medium">{r.label}</td>
                            <td className="text-right px-4 py-3">{r.v25.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                            <td className="text-right px-4 py-3">{r.v26.toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                            <td className={`text-right px-4 py-3 font-semibold ${cmp.direction === 'up' ? 'text-green-400' : 'text-red-400'}`}>
                              {cmp.direction === 'up' ? '+' : ''}{cmp.pctChange.toFixed(1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()

            /* --- STANDARD & RANGE MODES --- */
            : (
              <>
                {/* Highlight Cards */}
                {latestMarketing && (comparisonMode === 'standard' || (comparisonMode === 'range' && rangeStart && rangeEnd)) && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className={`bg-gray-900 rounded-xl p-5 border ${comparisonMode === 'range' ? 'border-purple-800/50' : 'border-gray-800'}`}>
                      <p className="text-sm text-gray-400">
                        {comparisonMode === 'range' ? 'Range Total' : ''} Reddit Impressions
                      </p>
                      <p className="text-2xl font-bold text-orange-400">
                        {comparisonMode === 'range'
                          ? formatWholeNumber(filteredMarketing.reduce((s, d) => s + d.reddit.impressions, 0))
                          : formatWholeNumber(latestMarketing.reddit.impressions)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {comparisonMode === 'range' ? `${filteredMarketing.length} weeks` :
                          (prevMarketing ? getPercentChange(latestMarketing.reddit.impressions, prevMarketing.reddit.impressions) + ' vs prev' : '')}
                      </p>
                    </div>
                    <div className={`bg-gray-900 rounded-xl p-5 border ${comparisonMode === 'range' ? 'border-purple-800/50' : 'border-gray-800'}`}>
                      <p className="text-sm text-gray-400">Reddit Posts</p>
                      <p className="text-2xl font-bold text-orange-300">
                        {comparisonMode === 'range'
                          ? filteredMarketing.reduce((s, d) => s + d.reddit.posts, 0)
                          : latestMarketing.reddit.posts}
                      </p>
                    </div>
                    <div className={`bg-gray-900 rounded-xl p-5 border ${comparisonMode === 'range' ? 'border-purple-800/50' : 'border-gray-800'}`}>
                      <p className="text-sm text-gray-400">Threads Followers</p>
                      <p className="text-2xl font-bold text-blue-400">
                        {comparisonMode === 'range'
                          ? formatWholeNumber(filteredMarketing.reduce((s, d) => s + d.threads.followersGained, 0))
                          : formatWholeNumber(latestMarketing.threads.totalFollowers)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {comparisonMode === 'range' ? 'total gained' : `+${formatWholeNumber(latestMarketing.threads.followersGained)} this week`}
                      </p>
                    </div>
                    <div className={`bg-gray-900 rounded-xl p-5 border ${comparisonMode === 'range' ? 'border-purple-800/50' : 'border-gray-800'}`}>
                      <p className="text-sm text-gray-400">Reels Views</p>
                      <p className="text-2xl font-bold text-pink-400">
                        {comparisonMode === 'range'
                          ? formatWholeNumber(filteredMarketing.filter(d => d.reels).reduce((s, d) => s + d.reels.views, 0))
                          : (latestMarketing.reels ? formatWholeNumber(latestMarketing.reels.views) : 'N/A')}
                      </p>
                    </div>
                  </div>
                )}

                {/* X (Twitter) Section */}
                {(selectedMarketingPlatform === 'all' || selectedMarketingPlatform === 'x') && filteredMarketing.some(d => d.x) && (
                  <div className="mb-6">
                    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-4">
                      <h3 className="text-lg font-semibold mb-4 text-sky-400">X (Twitter) Organic</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={filteredMarketing.filter(d => d.x).map(d => ({
                          period: d.period,
                          Impressions: d.x.impressions,
                          Engagements: d.x.engagements
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="period" stroke="#9CA3AF" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                          <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                          <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                            formatter={(value) => value.toLocaleString()} />
                          <Legend />
                          <Area type="monotone" dataKey="Impressions" stroke="#38BDF8" fill="#38BDF8" fillOpacity={0.15} strokeWidth={2} />
                          <Area type="monotone" dataKey="Engagements" stroke="#7DD3FC" fill="#7DD3FC" fillOpacity={0.1} strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-800">
                              <th className="text-left px-4 py-3 text-gray-400 font-medium">Period</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Impressions</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Engagements</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Eng. Rate</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Profile Visits</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Followers</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredMarketing.filter(d => d.x).map((d) => (
                              <tr key={'x-' + d.period} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                                <td className="px-4 py-3 font-medium">{d.period}</td>
                                <td className="text-right px-4 py-3 text-sky-400">{formatWholeNumber(d.x.impressions)}</td>
                                <td className="text-right px-4 py-3 text-sky-300">{formatWholeNumber(d.x.engagements)}</td>
                                <td className="text-right px-4 py-3 text-gray-300">{d.x.engagementRate}%</td>
                                <td className="text-right px-4 py-3 text-gray-300">{formatWholeNumber(d.x.profileVisits)}</td>
                                <td className="text-right px-4 py-3 font-semibold">{formatWholeNumber(d.x.totalFollowers)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reddit Section */}
                {(selectedMarketingPlatform === 'all' || selectedMarketingPlatform === 'reddit') && (
                  <div className="mb-6">
                    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-4">
                      <h3 className="text-lg font-semibold mb-4 text-orange-400">Reddit Campaign</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={filteredMarketing.map(d => ({
                          period: d.period,
                          Impressions: d.reddit.impressions,
                          Upvotes: d.reddit.upvotes
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="period" stroke="#9CA3AF" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                          <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                          <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                            formatter={(value) => value.toLocaleString()} />
                          <Legend />
                          <Area type="monotone" dataKey="Impressions" stroke="#FB923C" fill="#FB923C" fillOpacity={0.15} strokeWidth={2} />
                          <Area type="monotone" dataKey="Upvotes" stroke="#FDBA74" fill="#FDBA74" fillOpacity={0.1} strokeWidth={2} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-800">
                              <th className="text-left px-4 py-3 text-gray-400 font-medium">Period</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Posts</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Upvotes</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Impressions</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Avg Upvotes</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Avg Impressions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredMarketing.map((d) => (
                              <tr key={'reddit-' + d.period} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                                <td className="px-4 py-3 font-medium">{d.period}</td>
                                <td className="text-right px-4 py-3">{d.reddit.posts}</td>
                                <td className="text-right px-4 py-3 text-orange-400">{formatWholeNumber(d.reddit.upvotes)}</td>
                                <td className="text-right px-4 py-3 text-orange-300">{formatWholeNumber(d.reddit.impressions)}</td>
                                <td className="text-right px-4 py-3 text-gray-300">{d.reddit.avgUpvotes}</td>
                                <td className="text-right px-4 py-3 text-gray-300">{formatWholeNumber(d.reddit.avgImpressions)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Threads Section */}
                {(selectedMarketingPlatform === 'all' || selectedMarketingPlatform === 'threads') && (
                  <div className="mb-6">
                    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-4">
                      <h3 className="text-lg font-semibold mb-4 text-blue-400">Threads Campaign</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={filteredMarketing.map(d => ({
                          period: d.period,
                          'Total Followers': d.threads.totalFollowers,
                          'Followers Gained': d.threads.followersGained,
                          Likes: d.threads.likes
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="period" stroke="#9CA3AF" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                          <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                          <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                            formatter={(value) => value.toLocaleString()} />
                          <Legend />
                          <Line type="monotone" dataKey="Total Followers" stroke="#60A5FA" strokeWidth={2} dot={{ fill: '#60A5FA', r: 3 }} />
                          <Line type="monotone" dataKey="Followers Gained" stroke="#34D399" strokeWidth={2} dot={{ fill: '#34D399', r: 3 }} />
                          <Line type="monotone" dataKey="Likes" stroke="#A78BFA" strokeWidth={2} dot={{ fill: '#A78BFA', r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-800">
                              <th className="text-left px-4 py-3 text-gray-400 font-medium">Period</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Posts</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Followers +</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Likes</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Comments</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Total Followers</th>
                              <th className="text-left px-4 py-3 text-gray-400 font-medium">Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredMarketing.map((d) => (
                              <tr key={'threads-' + d.period} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                                <td className="px-4 py-3 font-medium">{d.period}</td>
                                <td className="text-right px-4 py-3">{d.threads.posts}</td>
                                <td className="text-right px-4 py-3 text-green-400">{formatWholeNumber(d.threads.followersGained)}</td>
                                <td className="text-right px-4 py-3 text-blue-400">{formatWholeNumber(d.threads.likes)}</td>
                                <td className="text-right px-4 py-3 text-purple-400">{formatWholeNumber(d.threads.comments)}</td>
                                <td className="text-right px-4 py-3 font-semibold">{formatWholeNumber(d.threads.totalFollowers)}</td>
                                <td className="px-4 py-3 text-xs text-yellow-400 max-w-xs truncate">
                                  {d.note && (
                                    <span className="flex items-center gap-1">
                                      <AlertTriangle size={12} />
                                      {d.note}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reels Section */}
                {(selectedMarketingPlatform === 'all' || selectedMarketingPlatform === 'reels') && (
                  <div className="mb-6">
                    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-4">
                      <h3 className="text-lg font-semibold mb-4 text-pink-400">Reels Campaign</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={filteredMarketing.filter(d => d.reels).map(d => ({
                          period: d.period,
                          Views: d.reels.views,
                          Likes: d.reels.likes,
                          'Followers +': d.reels.followersGained
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="period" stroke="#9CA3AF" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                          <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                          <Tooltip contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px' }}
                            formatter={(value) => value.toLocaleString()} />
                          <Legend />
                          <Bar dataKey="Views" fill="#EC4899" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="Likes" fill="#F472B6" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="Followers +" fill="#34D399" radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-800">
                              <th className="text-left px-4 py-3 text-gray-400 font-medium">Period</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Posted</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Views</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Likes</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Comments</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Followers +</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Avg Views</th>
                              <th className="text-right px-4 py-3 text-gray-400 font-medium">Total Followers</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredMarketing.filter(d => d.reels).map((d) => (
                              <tr key={'reels-' + d.period} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                                <td className="px-4 py-3 font-medium">{d.period}</td>
                                <td className="text-right px-4 py-3">{d.reels.posted}</td>
                                <td className="text-right px-4 py-3 text-pink-400">{formatWholeNumber(d.reels.views)}</td>
                                <td className="text-right px-4 py-3 text-pink-300">{formatWholeNumber(d.reels.likes)}</td>
                                <td className="text-right px-4 py-3 text-purple-400">{formatWholeNumber(d.reels.comments)}</td>
                                <td className="text-right px-4 py-3 text-green-400">{formatWholeNumber(d.reels.followersGained)}</td>
                                <td className="text-right px-4 py-3 text-gray-300">{formatWholeNumber(d.reels.avgViews)}</td>
                                <td className="text-right px-4 py-3 font-semibold">{formatWholeNumber(d.reels.totalFollowers)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes Section */}
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <AlertTriangle size={18} className="text-yellow-400" /> Notes & Annotations
                  </h3>
                  <div className="space-y-3">
                    {filteredMarketing.filter(d => d.note).map((d) => (
                      <div key={'note-' + d.period} className="flex items-start gap-3 p-3 bg-gray-800/50 rounded-lg border border-gray-700/50">
                        <span className="text-xs font-mono text-gray-500 whitespace-nowrap mt-0.5">{d.period}</span>
                        <p className="text-sm text-yellow-300">{d.note}</p>
                      </div>
                    ))}
                    {filteredMarketing.filter(d => d.note).length === 0 && (
                      <p className="text-sm text-gray-500">No notes for the selected period.</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'activeAccounts' && (
          <ActiveAccountsPanel
            session={session}
            profile={profile}
            marketingData={allMarketingData}
            fallbackAccounts={activeAccounts}
            onAccountsChange={setActiveAccountRows}
          />
        )}

        {activeTab === 'accounts' && (() => {
          const accounts = managementAccounts;
          const nok = (v) => 'NOK ' + Math.round(v).toLocaleString();
          const ytdProfit = accounts.ytd.revenue - accounts.ytd.totalExpenses;
          const margin = (ytdProfit / accounts.ytd.revenue * 100);
          const monthlyWithProfit = accounts.monthly.map(m => ({
            ...m,
            profit: m.revenue - m.totalExpenses
          }));
          const expenseBreakdown = [
            { label: 'Agency (Aureum)', value: accounts.ytd.agency, colour: 'bg-red-500' },
            { label: 'Business Mgmt (Slack)', value: accounts.ytd.businessMgmt, colour: 'bg-purple-500' },
            { label: 'Tools & Business Purchases', value: accounts.ytd.tools, colour: 'bg-blue-500' },
            { label: 'Bank Fees', value: accounts.ytd.bankFees, colour: 'bg-gray-500' }
          ];
          return (
            <div>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Management accounts</p>
                  <h2 className="text-2xl font-serif text-white mb-1">Year-to-date summary</h2>
                  <p className="text-gray-400 text-sm">{accounts.period} · all figures in {accounts.currency} · mapped to Norwegian tax return (RF-1175)</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${managementAccountsStatus.source === 'google_sheets' ? 'border-green-500/40 bg-green-500/10 text-green-300' : 'border-amber-500/40 bg-amber-500/10 text-amber-200'}`}>
                    {managementAccountsStatus.source === 'google_sheets' ? 'Live Google Sheet' : 'Latest saved snapshot'}
                  </span>
                  <button
                    type="button"
                    onClick={() => refreshManagementAccounts(session?.access_token)}
                    disabled={!session?.access_token || managementAccountsStatus.loading}
                    className="inline-flex items-center gap-2 rounded-lg border border-purple-500/40 bg-purple-500/10 px-3 py-1.5 text-xs font-semibold text-purple-200 transition hover:bg-purple-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <RefreshCw size={14} className={managementAccountsStatus.loading ? 'animate-spin' : ''} />
                    {managementAccountsStatus.loading ? 'Syncing' : 'Refresh now'}
                  </button>
                </div>
              </div>

              {managementAccountsStatus.fetchedAt && (
                <p className="-mt-2 mb-4 text-xs text-gray-500">Last synced {new Date(managementAccountsStatus.fetchedAt).toLocaleString()}</p>
              )}
              {managementAccountsStatus.error && (
                <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                  Live refresh is temporarily unavailable, so the latest saved figures are shown. {managementAccountsStatus.error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Money in (revenue)</div>
                  <div className="text-3xl font-bold text-green-400">{nok(accounts.ytd.revenue)}</div>
                  <div className="text-xs text-gray-500 mt-1">All platforms — OnlyFans, Fenix, Kick</div>
                </div>
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Money out (costs)</div>
                  <div className="text-3xl font-bold text-red-400">{nok(accounts.ytd.totalExpenses)}</div>
                  <div className="text-xs text-gray-500 mt-1">Agency, management, tools, banking</div>
                </div>
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">What's left (profit)</div>
                  <div className="text-3xl font-bold text-white">{nok(ytdProfit)}</div>
                  <div className="text-xs text-gray-500 mt-1">{margin.toFixed(1)}% margin · before Norwegian tax</div>
                </div>
              </div>

              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-4">
                <h3 className="font-bold mb-4">Month by month</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={monthlyWithProfit}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="month" stroke="#9ca3af" tick={{ fontSize: 12 }} />
                    <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} tickFormatter={(v) => 'kr' + (v / 1000).toFixed(0) + 'k'} />
                    <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151' }} formatter={(v) => nok(v)} />
                    <Legend />
                    <Bar dataKey="revenue" name="Revenue" fill="#10b981" />
                    <Bar dataKey="totalExpenses" name="Expenses" fill="#ef4444" />
                    <Bar dataKey="profit" name="Profit" fill="#a855f7" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                  <h3 className="font-bold mb-4">Where the money goes</h3>
                  <div className="space-y-3">
                    {expenseBreakdown.map(e => {
                      const pct = (e.value / accounts.ytd.totalExpenses * 100);
                      return (
                        <div key={e.label}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-300">{e.label}</span>
                            <span className="text-gray-400">{nok(e.value)} · {pct.toFixed(1)}%</span>
                          </div>
                          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                            <div className={`h-full ${e.colour}`} style={{ width: pct + '%' }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
                  <h3 className="font-bold mb-4">Plain-English summary</h3>
                  <ul className="text-sm text-gray-300 space-y-3">
                    <li>• For every <strong>kr 100</strong> earned, you keep roughly <strong className="text-green-400">kr {margin.toFixed(0)}</strong> before tax.</li>
                    <li>• The agency (Aureum) is your <strong>biggest cost</strong> at <strong>{(accounts.ytd.agency / accounts.ytd.totalExpenses * 100).toFixed(0)}%</strong> of spend.</li>
                    <li>• <strong>VAT reclaim</strong> already captured: {nok(accounts.ytd.vatReclaim)}.</li>
                    <li>• At the current run-rate, annualised revenue is <strong>~{nok(accounts.ytd.revenue / accounts.monthly.length * 12)}</strong>.</li>
                    <li>• Projected annual profit at this pace: <strong className="text-white">~{nok(ytdProfit / accounts.monthly.length * 12)}</strong>.</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 overflow-x-auto">
                <h3 className="font-bold mb-4">Detail (monthly)</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-700 text-left">
                      <th className="py-2 font-semibold">Line</th>
                      {accounts.monthly.map(m => <th key={m.month} className="py-2 text-right font-semibold">{m.month}</th>)}
                      <th className="py-2 text-right font-semibold text-green-400">YTD</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 text-green-400">Revenue</td>
                      {accounts.monthly.map(m => <td key={m.month} className="py-2 text-right">{nok(m.revenue)}</td>)}
                      <td className="py-2 text-right font-bold">{nok(accounts.ytd.revenue)}</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 text-gray-400 pl-3">Agency</td>
                      {accounts.monthly.map(m => <td key={m.month} className="py-2 text-right">-{nok(m.agency)}</td>)}
                      <td className="py-2 text-right font-bold">-{nok(accounts.ytd.agency)}</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 text-gray-400 pl-3">Business Mgmt</td>
                      {accounts.monthly.map(m => <td key={m.month} className="py-2 text-right">-{nok(m.businessMgmt)}</td>)}
                      <td className="py-2 text-right font-bold">-{nok(accounts.ytd.businessMgmt)}</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 text-gray-400 pl-3">Tools & purchases</td>
                      {accounts.monthly.map(m => <td key={m.month} className="py-2 text-right">-{nok(m.tools)}</td>)}
                      <td className="py-2 text-right font-bold">-{nok(accounts.ytd.tools)}</td>
                    </tr>
                    <tr className="border-b border-gray-800">
                      <td className="py-2 text-gray-400 pl-3">Bank fees</td>
                      {accounts.monthly.map(m => <td key={m.month} className="py-2 text-right">-{nok(m.bankFees)}</td>)}
                      <td className="py-2 text-right font-bold">-{nok(accounts.ytd.bankFees)}</td>
                    </tr>
                    <tr className="border-b-2 border-gray-700">
                      <td className="py-2 text-red-400 font-semibold">Total expenses</td>
                      {accounts.monthly.map(m => <td key={m.month} className="py-2 text-right">-{nok(m.totalExpenses)}</td>)}
                      <td className="py-2 text-right font-bold">-{nok(accounts.ytd.totalExpenses)}</td>
                    </tr>
                    <tr>
                      <td className="py-3 font-bold">Profit</td>
                      {accounts.monthly.map(m => {
                        const p = m.revenue - m.totalExpenses;
                        return <td key={m.month} className={`py-3 text-right font-bold ${p >= 0 ? 'text-white' : 'text-red-400'}`}>{p >= 0 ? nok(p) : '-' + nok(Math.abs(p))}</td>;
                      })}
                      <td className="py-3 text-right font-bold text-green-400">{nok(ytdProfit)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {activeTab === 'cyprus' && (() => {
          const rev = cypRev, norR = cypNorRent, cypR = cypCypRent;
          const norA = norR * 12, cypA = cypR * 12;
          const ad = rev - rev * 0.52 - rev * 0.10 - 10000;
          const nP = Math.max(0, ad);
          // Norway effective tax: progressive brackets, with a 43% floor so
          // the rate never dips below 43% regardless of revenue slider position.
          const NOR_MIN_RATE = 0.43;
          const NOR_CAL = 1.0987;
          const nT = calcNorTax(nP);
          const norTax = Math.max(nP * NOR_MIN_RATE, nT.total * NOR_CAL);
          const nAT = nP - norTax;
          const nCash = nAT - norA;
          const nEff = nP > 0 ? (norTax / nP * 100) : 0;
          const nMarg = getMarginal(nP) * 100;
          const cTP = Math.max(0, ad - cypA - 6000 - 1620);
          const cTax = cTP * 0.15;
          const cDiv = cTP - cTax;
          const cCash = 6000 + cDiv - 9470;
          const diff = cCash - nCash;
          const tSave = norTax - cTax;

          const rateChartData = [];
          for (let p = 10000; p <= 250000; p += 5000) {
            const nt = calcNorTax(p);
            rateChartData.push({
              profit: `€${p / 1000}k`,
              Norway: p > 0 ? +((nt.total / p) * 100).toFixed(1) : 0,
              Cyprus: 15
            });
          }

          const growth = (revBase) => {
            const adj = Math.max(0, revBase - revBase * 0.52 - revBase * 0.10 - 10000);
            const ntx = calcNorTax(adj);
            const norwayTotal = Math.max(adj * NOR_MIN_RATE, ntx.total * NOR_CAL);
            const ctx = adj * 0.15;
            return { norway: norwayTotal, cyprus: ctx, save: norwayTotal - ctx };
          };
          const g1 = growth(271000), g2 = growth(325000), g3 = growth(407000), g4 = growth(542000);

          const sectionCls = "bg-gray-900 rounded-xl p-6 border border-gray-800";
          const labelCls = "text-xs text-gray-500 uppercase tracking-wider mb-2";
          const rowCls = "flex justify-between py-2 text-sm border-b border-gray-800 last:border-0";

          return (
            <div className="space-y-8">
              {/* Header */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">Confidential — prepared by Adam Slack</p>
                <h1 className="text-4xl font-serif text-white mb-2">Cyprus relocation</h1>
                <p className="text-gray-400 text-lg max-w-2xl">A financial overview of relocating your business from Norway to Cyprus — what changes, what it costs, and how much you keep.</p>
              </div>

              {/* Interactive calculator — at top */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Your numbers</p>
                <h2 className="text-2xl font-serif text-white mb-2">Interactive comparison</h2>
                <p className="text-gray-400 text-sm mb-4 max-w-2xl">Adjust the sliders to see exactly how different scenarios play out. Rent in Norway is paid from after-tax income. Rent in Cyprus is a deductible business expense.</p>

                {/* Sliders */}
                <div className={sectionCls + " mb-4"}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="md:col-span-3">
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-sm text-gray-400">Annual gross revenue</span>
                        <span className="text-xs text-red-400">Norway effective rate: <span className="font-bold">{nEff.toFixed(1)}%</span> · marginal: <span className="font-bold">{nMarg.toFixed(1)}%</span></span>
                      </div>
                      <input type="range" min="150000" max="800000" step="10000" value={cypRev} onChange={(e) => setCypRev(+e.target.value)} className="w-full accent-green-500" />
                      <div className="flex justify-between text-xs text-gray-500"><span>€150k</span><span className="text-white font-bold">{eur(cypRev)}</span><span>€800k</span></div>
                      <p className="text-xs text-gray-500 mt-1">Norway's tax rate is progressive — it automatically rises with income.</p>
                    </div>
                    <div className="md:col-span-3 border-t border-gray-800"></div>
                    <div>
                      <div className="text-sm text-gray-400 mb-1">Norway rent (€/month)</div>
                      <input type="range" min="0" max="3500" step="100" value={cypNorRent} onChange={(e) => setCypNorRent(+e.target.value)} className="w-full accent-red-500" />
                      <div className="flex justify-between text-xs text-gray-500"><span>€0 (dad's)</span><span className="text-white font-bold">{eur(cypNorRent)}</span><span>€3,500</span></div>
                      <p className="text-xs text-gray-500 mt-1">3-bed average: €1,400–2,500. Not deductible.</p>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400 mb-1">Cyprus rent (€/month)</div>
                      <input type="range" min="1000" max="5000" step="100" value={cypCypRent} onChange={(e) => setCypCypRent(+e.target.value)} className="w-full accent-green-500" />
                      <div className="flex justify-between text-xs text-gray-500"><span>€1,000</span><span className="text-white font-bold">{eur(cypCypRent)}</span><span>€5,000</span></div>
                      <p className="text-xs text-gray-500 mt-1">Modern 3-bed villa: €1,500–3,500. Fully deductible.</p>
                    </div>
                    <div className="flex flex-col justify-center text-xs text-gray-500 leading-relaxed">
                      Rent in Norway is paid from after-tax income. Rent in Cyprus is a <span className="text-green-400 font-semibold">deductible business expense</span>, effectively making it 15% cheaper.
                    </div>
                  </div>
                </div>

                {/* Side-by-side comparison */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={sectionCls}>
                    <div className="text-sm font-bold text-red-400 border-b-2 border-gray-700 pb-2 mb-2">Norway</div>
                    <div className={rowCls}><span className="text-gray-400">Revenue</span><span>{eur(rev)}</span></div>
                    <div className={rowCls}><span className="text-gray-400">Agency (52%)</span><span>-{eur(rev * 0.52)}</span></div>
                    <div className={rowCls}><span className="text-gray-400">Management fee (10%)</span><span>-{eur(rev * 0.10)}</span></div>
                    <div className={rowCls}><span className="text-gray-400">Business expenses</span><span>-€10,000</span></div>
                    <div className={rowCls + " font-semibold"}><span>Taxable profit</span><span>{eur(nP)}</span></div>
                    <div className={rowCls + " font-semibold text-red-400"}><span>Tax ({nEff.toFixed(1)}% effective)</span><span>-{eur(norTax)}</span></div>
                    <div className={rowCls}><span className="text-gray-400">After tax</span><span>{eur(nAT)}</span></div>
                    <div className={rowCls}><span className="text-gray-400">Rent (from pocket)</span><span>{norR === 0 ? '€0 (free)' : '-' + eur(norA)}</span></div>
                    <div className="flex justify-between py-3 font-bold border-t-2 border-gray-700 mt-1"><span>Cash remaining</span><span>{eurS(nCash)}</span></div>
                  </div>
                  <div className={sectionCls}>
                    <div className="text-sm font-bold text-green-400 border-b-2 border-gray-700 pb-2 mb-2">Cyprus</div>
                    <div className={rowCls}><span className="text-gray-400">Revenue</span><span>{eur(rev)}</span></div>
                    <div className={rowCls}><span className="text-gray-400">Agency (52%)</span><span>-{eur(rev * 0.52)}</span></div>
                    <div className={rowCls}><span className="text-gray-400">Management fee (10%)</span><span>-{eur(rev * 0.10)}</span></div>
                    <div className={rowCls}><span className="text-gray-400">Business expenses</span><span>-€10,000</span></div>
                    <div className={rowCls}><span className="text-gray-400">Rent (deductible)</span><span>-{eur(cypA)}</span></div>
                    <div className={rowCls}><span className="text-gray-400">Salary + social</span><span>-€7,620</span></div>
                    <div className={rowCls + " font-semibold"}><span>Taxable profit</span><span>{eur(cTP)}</span></div>
                    <div className={rowCls + " font-semibold text-green-400"}><span>Corporate tax (15%)</span><span>-{eur(cTax)}</span></div>
                    <div className={rowCls}><span className="text-gray-400">Dividend (tax-free)</span><span>{eur(cDiv)}</span></div>
                    <div className={rowCls}><span className="text-gray-400">+ Salary</span><span>+€6,000</span></div>
                    <div className={rowCls}><span className="text-gray-400">GHS / health / audit</span><span>-€9,470</span></div>
                    <div className="flex justify-between py-3 font-bold border-t-2 border-gray-700 mt-1"><span>Cash remaining</span><span>{eurS(cCash)}</span></div>
                  </div>
                </div>

                {/* Result bar */}
                <div className={`rounded-xl p-5 flex justify-between items-center mt-4 ${diff >= 0 ? 'bg-green-950/40 border border-green-700' : 'bg-red-950/40 border border-red-700'}`}>
                  <div>
                    <div className={`text-sm ${diff >= 0 ? 'text-green-400' : 'text-red-400'}`}>{diff >= 0 ? 'Cyprus saves per year' : 'Norway cheaper per year'}</div>
                    <div className="text-xs text-gray-500 mt-1">{eur(Math.abs(diff) / 12)}/month {diff >= 0 ? 'better' : 'worse'} off</div>
                  </div>
                  <div className={`text-3xl font-bold ${diff >= 0 ? 'text-green-400' : 'text-red-400'}`}>{(diff >= 0 ? '+' : '-') + eur(Math.abs(diff))}</div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                  <div className={sectionCls + " text-center"}><div className={labelCls}>Norway tax</div><div className="text-xl font-bold">{eur(norTax)}</div></div>
                  <div className={sectionCls + " text-center"}><div className={labelCls}>Cyprus tax</div><div className="text-xl font-bold">{eur(cTax)}</div></div>
                  <div className={sectionCls + " text-center"}><div className={labelCls}>Tax saving</div><div className="text-xl font-bold text-green-400">{eur(tSave)}</div></div>
                </div>
              </div>

              {/* Hero stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={sectionCls + " text-center"}>
                  <div className={labelCls}>Current tax rate (Norway)</div>
                  <div className="text-4xl font-bold text-red-400">43%</div>
                  <div className="text-xs text-gray-500 mt-1">progressive, rising with income</div>
                </div>
                <div className={sectionCls + " text-center"}>
                  <div className={labelCls}>Cyprus tax rate</div>
                  <div className="text-4xl font-bold text-green-400">15%</div>
                  <div className="text-xs text-gray-500 mt-1">flat, regardless of income</div>
                </div>
                <div className={sectionCls + " text-center"}>
                  <div className={labelCls}>Dividends under non-dom</div>
                  <div className="text-4xl font-bold text-green-400">0%</div>
                  <div className="text-xs text-gray-500 mt-1">tax-free for up to 17 years</div>
                </div>
              </div>

              {/* Tax rate chart */}
              <div className={sectionCls}>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">The gap widens with success</p>
                <h2 className="text-2xl font-serif text-white mb-2">Norway vs Cyprus tax rates</h2>
                <p className="text-gray-400 text-sm mb-4">As your income grows, Norway takes progressively more. Cyprus stays flat.</p>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={rateChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="profit" stroke="#9ca3af" tick={{ fontSize: 11 }} interval={4} />
                    <YAxis stroke="#9ca3af" tick={{ fontSize: 11 }} tickFormatter={(v) => v + '%'} domain={[0, 55]} />
                    <Tooltip contentStyle={{ backgroundColor: '#111827', border: '1px solid #374151' }} formatter={(v) => v + '%'} />
                    <Legend />
                    <Line type="monotone" dataKey="Norway" stroke="#E24B4A" strokeWidth={2.5} dot={false} />
                    <Line type="monotone" dataKey="Cyprus" stroke="#10b981" strokeWidth={2.5} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Norway problem */}
              <div className={sectionCls}>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">The problem</p>
                <h2 className="text-2xl font-serif text-white mb-2">Norway punishes growth</h2>
                <p className="text-gray-400 text-sm mb-4 max-w-2xl">As a sole proprietor (ENK), your income is taxed personally with three taxes stacked on top of each other. The more you earn, the more they take.</p>
                <div className="mt-4">
                  <div className="flex justify-between py-3 text-sm font-bold border-b-2 border-gray-700"><span>Tax layer</span><span>Rate</span></div>
                  <div className={rowCls}><span className="text-gray-400">Base income tax</span><span>22.0% on everything</span></div>
                  <div className={rowCls}><span className="text-gray-400">National insurance (trygdeavgift)</span><span>10.8% on everything</span></div>
                  <div className={rowCls}><span className="text-gray-400">Trinnskatt bracket 1 (NOK 226k–318k)</span><span>+1.7%</span></div>
                  <div className={rowCls}><span className="text-gray-400">Trinnskatt bracket 2 (NOK 318k–725k)</span><span>+4.0%</span></div>
                  <div className={rowCls}><span className="text-gray-400">Trinnskatt bracket 3 (NOK 725k–980k)</span><span>+13.7%</span></div>
                  <div className={rowCls}><span className="text-gray-400">Trinnskatt bracket 4 (NOK 980k–1.47M)</span><span>+16.8%</span></div>
                  <div className={rowCls}><span className="text-gray-400">Trinnskatt bracket 5 (above NOK 1.47M)</span><span>+17.8%</span></div>
                  <div className="flex justify-between py-3 text-sm font-bold border-t-2 border-gray-700 mt-2"><span>Marginal rate at your current income</span><span className="text-red-400">49.6%</span></div>
                </div>
                <div className="mt-4 p-4 bg-red-950/30 border-l-4 border-red-500 rounded text-sm text-gray-300">
                  At your current profit level (~NOK 1M), every additional krone you earn is taxed at nearly 50 øre. If your revenue doubles, the extra income is taxed even more aggressively.
                </div>
              </div>

              {/* Cyprus solution */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">The solution</p>
                <h2 className="text-2xl font-serif text-white mb-2">How Cyprus works</h2>
                <p className="text-gray-400 text-sm mb-4 max-w-2xl">A Cyprus limited company (LTD) combined with non-domicile tax status creates a simple, flat structure that doesn't penalise growth.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={sectionCls}>
                    <h3 className="font-bold mb-3">The company</h3>
                    <div className={rowCls}><span className="text-gray-400">Corporate tax</span><span className="text-green-400">15% flat</span></div>
                    <div className={rowCls}><span className="text-gray-400">On revenue of €100k</span><span>€15,000 tax</span></div>
                    <div className={rowCls}><span className="text-gray-400">On revenue of €500k</span><span>€75,000 tax</span></div>
                    <div className={rowCls}><span className="text-gray-400">On revenue of €1M</span><span>€150,000 tax</span></div>
                    <p className="text-xs text-gray-500 mt-3">The rate never changes. No brackets, no stacking, no surprises.</p>
                  </div>
                  <div className={sectionCls}>
                    <h3 className="font-bold mb-3">Your personal tax</h3>
                    <div className={rowCls}><span className="text-gray-400">Dividend income</span><span className="text-green-400">0% (non-dom)</span></div>
                    <div className={rowCls}><span className="text-gray-400">Interest income</span><span className="text-green-400">0% (non-dom)</span></div>
                    <div className={rowCls}><span className="text-gray-400">Minimum salary</span><span>€500/month</span></div>
                    <div className={rowCls}><span className="text-gray-400">Social contributions</span><span>~€135/month</span></div>
                    <div className={rowCls}><span className="text-gray-400">Health system (GHS)</span><span>2.65% capped at €4,770/yr</span></div>
                    <p className="text-xs text-gray-500 mt-3">Non-dom status lasts up to 17 years. Dividends from the LTD are completely tax-free on the personal side.</p>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-green-950/30 border-l-4 border-green-500 rounded text-sm text-gray-300">
                  Additionally, rent paid for your home is a <strong>fully deductible business expense</strong> through the LTD if you work from home — which you do. In Norway, personal rent is not deductible.
                </div>
              </div>

              {/* Consultation findings */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Professional advice</p>
                <h2 className="text-2xl font-serif text-white mb-2">What the tax specialists confirmed</h2>
                <p className="text-gray-400 text-sm mb-4 max-w-3xl">I met in person with an international tax and law firm in Cyprus that specialises in content creator relocations from Scandinavian countries.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={sectionCls}>
                    <h3 className="font-bold mb-3">The firm's credentials</h3>
                    <div className={rowCls}><span className="text-gray-400">Years in operation</span><span>16 years</span></div>
                    <div className={rowCls}><span className="text-gray-400">Companies managed in Cyprus</span><span>920+</span></div>
                    <div className={rowCls}><span className="text-gray-400">Individual clients</span><span>1,400+</span></div>
                    <div className={rowCls}><span className="text-gray-400">Non-dom applications submitted</span><span>1,600</span></div>
                    <div className={rowCls}><span className="text-gray-400">Non-dom approval rate</span><span className="text-green-400">100%</span></div>
                    <div className={rowCls}><span className="text-gray-400">Adult content creator experience</span><span className="text-green-400">Yes</span></div>
                  </div>
                  <div className={sectionCls}>
                    <h3 className="font-bold mb-3">Confirmed for your situation</h3>
                    <div className={rowCls}><span className="text-gray-400">LTD is the recommended structure</span><span className="text-green-400">✓</span></div>
                    <div className={rowCls}><span className="text-gray-400">Non-dom covers dividends</span><span className="text-green-400">✓</span></div>
                    <div className={rowCls}><span className="text-gray-400">OnlyFans / Fansly compatible</span><span className="text-green-400">✓</span></div>
                    <div className={rowCls}><span className="text-gray-400">Banks that accept adult content</span><span className="text-green-400">3 identified</span></div>
                    <div className={rowCls}><span className="text-gray-400">Norwegian exit procedure known</span><span className="text-green-400">✓</span></div>
                    <div className={rowCls}><span className="text-gray-400">EU citizen fast-track residency</span><span className="text-green-400">✓</span></div>
                  </div>
                </div>
                <div className={sectionCls + " mt-4"}>
                  <h3 className="font-bold mb-3">Key findings from the consultation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm text-gray-400">
                    <div>
                      <h4 className="text-green-400 font-semibold mb-1">Platform transition</h4>
                      <p className="mb-4">Your profile stays the same — your name remains on the account. The company name is added underneath. OnlyFans, Fansly, and Kick all support this structure. Once the Cyprus company tax ID and bank account are in place, you update the platform settings and payouts resume.</p>
                      <h4 className="text-green-400 font-semibold mb-1">Banking</h4>
                      <p className="mb-4">Three fintech banks were identified that officially work with adult content creators through Cyprus companies. Account opening takes 48–72 hours. All have mobile apps.</p>
                      <h4 className="text-green-400 font-semibold mb-1">Norwegian exit sequence</h4>
                      <p>The order is critical: deregister sole proprietorship first, then deregister private residence, then Cyprus incorporation is submitted. Written confirmation of closure is required before the Cyprus side proceeds.</p>
                    </div>
                    <div>
                      <h4 className="text-green-400 font-semibold mb-1">Investments & trading</h4>
                      <p className="mb-4">Long-term investments held personally are tax-free under non-dom — both dividends received and eventual sale proceeds. Active trading should go through the company at 15%. Crypto is taxed at a flat 8% from 2026 regardless of non-dom.</p>
                      <h4 className="text-green-400 font-semibold mb-1">Property purchase</h4>
                      <p className="mb-4">Rent for the first 6–12 months, then buy once you know the area. In the first 3 years of residency, mortgage requires 50% deposit. After 3 years, this drops to 30%.</p>
                      <h4 className="text-green-400 font-semibold mb-1">Moving the cats</h4>
                      <p>Norway's pet vaccination requirements are largely the same as Cyprus's. Bring the existing veterinary certificates. Standard EU transfer procedure.</p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-green-950/30 border-l-4 border-green-500 rounded text-sm text-gray-300">
                  The firm provides ongoing consultation at no additional per-call charge — it's included in the annual service package. All communication goes through me as your representative under a standard power of attorney.
                </div>
              </div>

              {/* Setup & costs */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Setup & costs</p>
                <h2 className="text-2xl font-serif text-white mb-2">What it costs to move</h2>
                <p className="text-gray-400 text-sm mb-4 max-w-2xl">One-time setup costs and ongoing annual costs.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={sectionCls}>
                    <h3 className="font-bold mb-3">One-time setup (year 1 only)</h3>
                    <div className={rowCls}><span className="text-gray-400">LTD incorporation</span><span>€2,740 + VAT</span></div>
                    <div className={rowCls}><span className="text-gray-400">Residence (Yellow Slip)</span><span>€350 + VAT</span></div>
                    <div className={rowCls}><span className="text-gray-400">Tax registration + non-dom</span><span>€2,450 + VAT</span></div>
                    <div className="flex justify-between py-3 font-bold border-t-2 border-gray-700 mt-1"><span>Total (inc. 19% VAT)</span><span>~€6,593</span></div>
                    <p className="text-xs text-gray-500 mt-3">Paid back through tax savings within 4–5 months.</p>
                  </div>
                  <div className={sectionCls}>
                    <h3 className="font-bold mb-3">Ongoing annual costs</h3>
                    <div className={rowCls}><span className="text-gray-400">Annual audit (independent)</span><span>~€1,500</span></div>
                    <div className={rowCls}><span className="text-gray-400">Social contributions</span><span>~€1,620</span></div>
                    <div className={rowCls}><span className="text-gray-400">GHS health contribution</span><span>~€4,770 (capped)</span></div>
                    <div className={rowCls}><span className="text-gray-400">Private health insurance</span><span>~€3,200</span></div>
                    <div className="flex justify-between py-3 font-bold border-t-2 border-gray-700 mt-1"><span>Total annual</span><span>~€11,090</span></div>
                    <p className="text-xs text-gray-500 mt-3">All statutory compliance, accounting, VAT filings and paperwork handled within the 10% management fee.</p>
                  </div>
                </div>
              </div>

              {/* Deductible */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Tax benefits</p>
                <h2 className="text-2xl font-serif text-white mb-2">What's deductible in Cyprus</h2>
                <p className="text-gray-400 text-sm mb-4 max-w-2xl">Cyprus allows significantly more aggressive expense deductions than Norway.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={sectionCls}>
                    <h3 className="font-bold text-green-400 mb-3">100% deductible</h3>
                    <div className={rowCls}><span>Rent / home office</span><span className="text-green-400">Full amount</span></div>
                    <div className={rowCls}><span>Flights & hotels</span><span className="text-green-400">Full amount</span></div>
                    <div className={rowCls}><span>Car rental</span><span className="text-green-400">Full amount</span></div>
                    <div className={rowCls}><span>Camera, phone, laptop</span><span className="text-green-400">Full amount</span></div>
                    <div className={rowCls}><span>Studio equipment & lighting</span><span className="text-green-400">Full amount</span></div>
                    <div className={rowCls}><span>Content trips (even mostly leisure)</span><span className="text-green-400">Full amount</span></div>
                    <div className={rowCls}><span>Restaurants (3–4x per week)</span><span className="text-green-400">~1% of profit</span></div>
                    <div className={rowCls}><span>Management fee</span><span className="text-green-400">Full amount</span></div>
                  </div>
                  <div className={sectionCls}>
                    <h3 className="font-bold text-red-400 mb-3">Not deductible</h3>
                    <div className={rowCls}><span>Clothing & shoes</span><span className="text-red-400">Personal</span></div>
                    <div className={rowCls}><span>Adult products</span><span className="text-red-400">Personal</span></div>
                    <div className={rowCls}><span>Personal grooming</span><span className="text-red-400">Personal</span></div>
                    <p className="text-xs text-gray-500 mt-3">No receipts or justification forms required for meals — just the card machine receipt. No daily spending caps.</p>
                  </div>
                </div>
              </div>

              {/* Growth scenarios */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Looking ahead</p>
                <h2 className="text-2xl font-serif text-white mb-2">As revenue grows, the saving accelerates</h2>
                <p className="text-gray-400 text-sm mb-4 max-w-2xl">Norway's progressive tax means the benefit of Cyprus compounds with success.</p>
                <div className={sectionCls + " overflow-x-auto"}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-700">
                        <th className="text-left py-3 font-bold">Scenario</th>
                        <th className="text-right py-3 font-bold">Norway tax</th>
                        <th className="text-right py-3 font-bold">Cyprus tax</th>
                        <th className="text-right py-3 font-bold text-green-400">Annual saving</th>
                        <th className="text-right py-3 font-bold text-green-400">5-year saving</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'Current (€271k revenue)', g: g1 },
                        { label: '20% growth (€325k)', g: g2 },
                        { label: '50% growth (€407k)', g: g3 },
                        { label: 'Double (€542k)', g: g4 }
                      ].map((row) => (
                        <tr key={row.label} className="border-b border-gray-800 last:border-0">
                          <td className="py-3">{row.label}</td>
                          <td className="text-right py-3 text-red-400">{eur(row.g.norway)}</td>
                          <td className="text-right py-3 text-green-400">{eur(row.g.cyprus)}</td>
                          <td className="text-right py-3 font-bold text-green-400">{eur(row.g.save)}</td>
                          <td className="text-right py-3 font-bold text-green-400">{eur(row.g.save * 5)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 p-4 bg-green-950/30 border-l-4 border-green-500 rounded text-sm text-gray-300">
                  These figures assume the same cost structure (52% agency, 10% management, €10k expenses). In practice, Cyprus deductions for travel, equipment, and meals would reduce the taxable profit further — making the real saving even larger.
                </div>
              </div>

              {/* Timeline */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">How it happens</p>
                <h2 className="text-2xl font-serif text-white mb-2">Transition timeline</h2>
                <p className="text-gray-400 text-sm mb-4 max-w-2xl">From decision to operational — approximately 8–12 weeks, with a 4–8 week payout gap to plan for.</p>
                <div className={sectionCls}>
                  <ol className="relative border-l-2 border-gray-700 ml-3 space-y-5">
                    {[
                      { t: 'Sign engagement & prepare documents', d: 'Company name application, memorandum drafted. Can be done remotely. 1 week.' },
                      { t: 'Deregister in Norway', d: 'Close sole proprietorship, deregister private residence. Get written confirmation. Variable timing.' },
                      { t: 'LTD incorporation submitted', d: 'Once Norwegian deregistration is confirmed. Takes 2–3 weeks for approval.' },
                      { t: 'Arrive in Cyprus', d: 'Yellow Slip (residence) done in person at immigration centre — 1 day in Larnaca.' },
                      { t: 'Tax registrations & bank account', d: 'Company TIC, VAT number, business bank account (48–72 hours). 2–3 weeks total.' },
                      { t: 'Platform switchover', d: 'Update OnlyFans/Fansly with Cyprus LTD details. Payouts resume. Non-dom applied for.' }
                    ].map((step, i) => (
                      <li key={i} className="ml-6">
                        <span className="absolute -left-2.5 w-5 h-5 rounded-full bg-green-500 border-4 border-gray-900"></span>
                        <h4 className="font-bold text-white">{step.t}</h4>
                        <p className="text-sm text-gray-400 mt-1">{step.d}</p>
                      </li>
                    ))}
                  </ol>
                </div>
                <div className="mt-4 p-4 bg-red-950/30 border-l-4 border-red-500 rounded text-sm text-gray-300">
                  <strong>Important:</strong> There will be a 4–8 week gap where payouts cannot be made while the company and bank account are being set up. Savings need to cover this period. Content can continue running — only cash withdrawals are paused.
                </div>
              </div>

              {/* Summary */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Summary</p>
                <h2 className="text-2xl font-serif text-white mb-4">The bottom line</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={sectionCls + " text-center py-8"}>
                    <div className={labelCls}>Year 1 setup cost</div>
                    <div className="text-4xl font-serif text-white my-2">€6,593</div>
                    <div className="text-xs text-gray-500">one-time, paid back in months</div>
                  </div>
                  <div className={sectionCls + " text-center py-8"}>
                    <div className={labelCls}>Annual tax saving</div>
                    <div className="text-4xl font-serif text-green-400 my-2">{eur(g1.save)}+</div>
                    <div className="text-xs text-gray-500">at current revenue, growing with income</div>
                  </div>
                  <div className={sectionCls + " text-center py-8"}>
                    <div className={labelCls}>5-year saving</div>
                    <div className="text-4xl font-serif text-green-400 my-2">{eur(g1.save * 5)}+</div>
                    <div className="text-xs text-gray-500">before accounting for growth</div>
                  </div>
                </div>
                <div className={sectionCls + " mt-4 max-w-xl"}>
                  <h3 className="font-bold mb-3">What doesn't change</h3>
                  <div className={rowCls}><span className="text-gray-400">Your management fee</span><span>10% — same as now</span></div>
                  <div className={rowCls}><span className="text-gray-400">Agency relationship</span><span>Continues unchanged</span></div>
                  <div className={rowCls}><span className="text-gray-400">Content workflow</span><span>No disruption</span></div>
                  <div className={rowCls}><span className="text-gray-400">Platform accounts</span><span>Same profiles, company name added</span></div>
                  <div className={rowCls}><span className="text-gray-400">Day-to-day admin</span><span>I handle everything</span></div>
                </div>
                <div className="mt-4 p-4 bg-green-950/30 border-l-4 border-green-500 rounded text-sm text-gray-300">
                  The only action required from you is: save enough to cover the transition gap (6–8 weeks), sign the engagement documents, and be physically present in Cyprus for one day for the Yellow Slip appointment. Everything else is managed on your behalf.
                </div>
              </div>
            </div>
          );
        })()}

        {activeTab === 'savings' && (() => {
          const FX = 11.5;
          const nok = (v) => 'kr ' + Math.round(v).toLocaleString();
          const rentNok = savRent * FX;

          // A. Moving costs (NOK) — now includes 3-month rental deposit
          const deposit = rentNok * 3;
          const moving = {
            flights: 19975,
            shipping: 44650,
            translation: 4700,
            ltdSetup: Math.round(2740 * 1.19 * FX),      // LTD incorporation + VAT
            yellowSlip: Math.round(350 * 1.19 * FX),     // Yellow Slip + VAT
            taxNonDom: Math.round(2450 * 1.19 * FX),     // Tax reg + non-dom + VAT
            deposit: deposit,                             // 3 months rent (adjusts with rent slider)
          };
          const movingSubtotal = Object.values(moving).reduce((a, b) => a + b, 0);
          const movingBuffer = Math.round(movingSubtotal * 0.10);
          const movingTotal = movingSubtotal + movingBuffer;

          // C. 6-month safety fund — ALL monthly expenses
          const monthly = {
            rent: rentNok,
            utilities: 2938,
            groceries: 6463,
            transport: 2820,
            healthInsurance: 1763,
            misc: 4113,
            tax: 14979,                                   // CY Ltd + Non-Dom incl GESY
            annualAuditMonthly: Math.round(1500 * FX / 12), // annual audit spread monthly
          };
          const monthlyTotal = Object.values(monthly).reduce((a, b) => a + b, 0);
          const safetyFund = monthlyTotal * 6;

          const goal = movingTotal + safetyFund;
          const saved = savSavings;
          const remaining = Math.max(0, goal - saved);
          const pct = Math.min(100, (saved / goal) * 100);
          const weeksToGoal = savWeekly > 0 ? remaining / savWeekly : 0;
          const monthsToGoal = weeksToGoal / 4.333;
          const goalDate = new Date(Date.now() + weeksToGoal * 7 * 86400000);

          const sectionCls = "bg-gray-900 rounded-xl p-6 border border-gray-800";
          const labelCls = "text-xs text-gray-500 uppercase tracking-wider mb-2";
          const rowCls = "flex justify-between py-2 text-sm border-b border-gray-800 last:border-0";

          return (
            <div className="space-y-6">
              {/* Header */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-widest mb-2">Savings Goal</p>
                <h1 className="text-4xl font-serif text-white mb-2">How close we are to the goal</h1>
                <p className="text-gray-400 text-lg max-w-2xl">Live tracker for Cyprus relocation savings — slide the rent to see how the target shifts. All figures in NOK.</p>
              </div>

              {/* PROGRESS HERO */}
              <div className="bg-gradient-to-br from-green-950/50 to-gray-900 rounded-xl p-8 border border-green-800/50">
                <div className="flex flex-col md:flex-row items-center md:items-stretch gap-6">
                  {/* Big % */}
                  <div className="flex-1 text-center">
                    <div className="text-xs text-green-400 uppercase tracking-widest mb-2">Progress</div>
                    <div className="text-7xl font-serif text-green-400 leading-none">{pct.toFixed(1)}%</div>
                    <div className="text-xs text-gray-500 mt-2">reached toward goal</div>
                  </div>
                  {/* Bar */}
                  <div className="flex-[2] flex flex-col justify-center w-full">
                    <div className="flex justify-between text-xs text-gray-400 mb-2">
                      <span>{nok(saved)} saved</span>
                      <span>{nok(goal)} goal</span>
                    </div>
                    <div className="h-6 bg-gray-800 rounded-full overflow-hidden relative">
                      <div className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500" style={{ width: pct + '%' }}></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-2">
                      <span className="text-red-400">{nok(remaining)} to go</span>
                      <span>~{weeksToGoal.toFixed(0)} weeks ({monthsToGoal.toFixed(1)} months) at current pace</span>
                    </div>
                    <div className="text-center text-sm text-gray-500 mt-4">
                      Expected goal date: <span className="text-white font-bold text-2xl md:text-3xl">{goalDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sliders / inputs */}
              <div className={sectionCls}>
                <h3 className="font-bold mb-4">Adjust the inputs</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Monthly rent (EUR)</div>
                    <input type="range" min="1000" max="5000" step="50" value={savRent} onChange={(e) => setSavRent(+e.target.value)} className="w-full accent-green-500" />
                    <div className="flex justify-between text-xs text-gray-500"><span>€1,000</span><span className="text-white font-bold">€{savRent.toLocaleString()}</span><span>€5,000</span></div>
                    <p className="text-xs text-gray-500 mt-1">= {nok(rentNok)}/month at {FX} NOK/EUR</p>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Current savings (NOK)</div>
                    <input type="range" min="0" max="1000000" step="1000" value={savSavings} onChange={(e) => setSavSavings(+e.target.value)} className="w-full accent-green-500" />
                    <div className="flex justify-between text-xs text-gray-500"><span>0</span><span className="text-white font-bold">{nok(savSavings)}</span><span>kr 1M</span></div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Weekly saving rate (NOK)</div>
                    <input type="range" min="0" max="20000" step="500" value={savWeekly} onChange={(e) => setSavWeekly(+e.target.value)} className="w-full accent-green-500" />
                    <div className="flex justify-between text-xs text-gray-500"><span>0</span><span className="text-white font-bold">{nok(savWeekly)}/wk</span><span>kr 20k</span></div>
                    <p className="text-xs text-gray-500 mt-1">= {nok(savWeekly * 4.333)}/month</p>
                  </div>
                </div>
              </div>

              {/* TWO BUCKETS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={sectionCls}>
                  <div className={labelCls}>A · Moving costs (incl. 3-month deposit)</div>
                  <div className="text-3xl font-bold text-white mb-1">{nok(movingTotal)}</div>
                  <div className="text-xs text-gray-500">One-time costs to get you to Cyprus — adjusts with rent</div>
                </div>
                <div className={sectionCls}>
                  <div className={labelCls}>B · 6-month safety fund</div>
                  <div className="text-3xl font-bold text-white mb-1">{nok(safetyFund)}</div>
                  <div className="text-xs text-gray-500">Cushion to cover everything if income pauses</div>
                </div>
              </div>

              {/* DETAIL BREAKDOWNS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Moving */}
                <div className={sectionCls}>
                  <h3 className="font-bold mb-3 text-white">A · Moving costs breakdown</h3>
                  <div className={rowCls}><span className="text-gray-400">Flights + cat transport + pet fees</span><span>{nok(moving.flights)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Shipping + customs</span><span>{nok(moving.shipping)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Translation / apostille</span><span>{nok(moving.translation)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">LTD incorporation (inc. 19% VAT)</span><span>{nok(moving.ltdSetup)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Yellow Slip residence (inc. VAT)</span><span>{nok(moving.yellowSlip)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Tax registration + non-dom (inc. VAT)</span><span>{nok(moving.taxNonDom)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">3-month rental deposit (live from slider)</span><span>{nok(moving.deposit)}</span></div>
                  <div className={rowCls + " font-semibold"}><span>Subtotal</span><span>{nok(movingSubtotal)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Misc (10% buffer)</span><span>{nok(movingBuffer)}</span></div>
                  <div className="flex justify-between py-3 font-bold border-t-2 border-gray-700 mt-1 text-green-400"><span>Total</span><span>{nok(movingTotal)}</span></div>
                </div>

                {/* Monthly */}
                <div className={sectionCls}>
                  <h3 className="font-bold mb-3 text-white">Monthly expenses (drives safety fund)</h3>
                  <div className={rowCls}><span className="text-gray-400">Rent (live from slider)</span><span>{nok(monthly.rent)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Utilities (electric, water, internet)</span><span>{nok(monthly.utilities)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Groceries & food</span><span>{nok(monthly.groceries)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Transportation</span><span>{nok(monthly.transport)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Private health insurance</span><span>{nok(monthly.healthInsurance)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Miscellaneous (phone, entertainment)</span><span>{nok(monthly.misc)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Monthly tax (CY Ltd + Non-Dom, incl GESY)</span><span>{nok(monthly.tax)}</span></div>
                  <div className={rowCls}><span className="text-gray-400">Annual audit (spread monthly)</span><span>{nok(monthly.annualAuditMonthly)}</span></div>
                  <div className={rowCls + " font-semibold"}><span>Monthly total</span><span>{nok(monthlyTotal)}</span></div>
                  <div className="flex justify-between py-3 font-bold border-t-2 border-gray-700 mt-1 text-green-400"><span>× 6 months safety fund</span><span>{nok(safetyFund)}</span></div>
                </div>
              </div>

              {/* GOAL SUMMARY */}
              <div className={sectionCls}>
                <h3 className="font-bold mb-3 text-white">Goal summary</h3>
                <div className={rowCls}><span className="text-gray-400">A · Moving costs (incl. 3-month deposit of {nok(deposit)})</span><span>{nok(movingTotal)}</span></div>
                <div className={rowCls}><span className="text-gray-400">B · 6-month safety fund</span><span>{nok(safetyFund)}</span></div>
                <div className="flex justify-between py-3 font-bold border-t-2 border-gray-700 mt-1"><span>TOTAL GOAL</span><span className="text-green-400 text-lg">{nok(goal)}</span></div>
                <div className={rowCls}><span className="text-gray-400">Current savings</span><span>{nok(saved)}</span></div>
                <div className={rowCls + " text-red-400"}><span>Remaining to save</span><span>{nok(remaining)}</span></div>
              </div>

              <div className="p-4 bg-green-950/30 border-l-4 border-green-500 rounded text-sm text-gray-300">
                Sources: moving/shipping/living figures from <strong>relocation_complete renting (2).xlsx</strong> (Cyprus column). Legal setup (LTD, Yellow Slip, tax reg + non-dom) from the Cyprus consultation figures with 19% VAT. Annual audit (~€1,500) added to monthly costs. Exchange rate fixed at 11.5 NOK/EUR.
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default App;
